// l10n status: check
import { action, flow, observable, computed, makeObservable, runInAction } from "mobx"
import { PlayerAbstract } from "store/page/player/PlayerAbstract"
import { rootStore } from "store/RootStore"
import { Math_floor } from "store/util/Math"
import { PlayerTracksAudioVjs, PlayerTracksSubtitleVjs } from "store/page/player/videojs/PlayerTrackVjs"
import { moment } from "store/util/moment"
import { Wait, WAIT_ABORT } from "store/util/Wait"
import { SourceTypeList } from "store/page/player/source/SourceTypeList"
import { ErrorBase } from "store/ErrorBase"
import { GenericError } from "store/GenericError"
import { TIME_FORMAT_BACKEND } from "store/model/Time"
import { EventEmitter } from "components/utils/EventEmitter"
import { isSafari, safariMainVersion, isIphone, isIpad, isMobileScreenSizeWidth, browserName, browserOs, isFairPlaySupported, isWidevineSupported, timeNow, sleep, isAndroid, getUrlQueryParam } from "utils/Utils"
import { appFeatures } from "AppFeatures"
import { SourceAdPlaylist } from "store/page/player/source/SourceAdPlaylist"
import { l10n } from "store/lang/L10n"

import videojs from "video.js"
// core does not work with videojs-http-streaming
//import videojs from 'video.js/dist/alt/video.core.js'
// For debugging vhs
// MTVW-731: Include for fullscreen freeze fix
// NOTE: when using EME v5.1.5, the following packages need to be added to package.json, otherwise the patched
// ./videojs-http-streaming.es.js cannot be loaaded properly:
// - @videojs/vhs-utils, m3u8-parser, mpd-parser, mux.js
import "./videojs-http-streaming.es.js"
// needed to hide controls, modal, etc.
import 'video.js/dist/video-js.css'
// overwrite distorting videojs css classes
import "./PlayerVjs.css"
// for drm
import videojsContribEme from "videojs-contrib-eme"
//import Ttml from "./videojs-ttml.js"

import { appStats, setQlWorkerHeaders } from "utils/TestSupport"
//import { monitorState } from "components/utils/ConnectionMonitor"
// MTVW-549
import { getQlHeaders } from "store/qlapi/QlHeaders"

const helperFixNaN = (v, defVal = 0) => (v && !Number.isNaN(v) ? v : defVal)

class PlayerTimeVal {
   value = 0
   _timeTs = null
   _timeCurrTs = null
   _name = null
   _callback = null
   defVal = null
   active = true

   constructor(name, callback, defVal = null) {
      this.name = name
      this._callback = callback
      this.defVal = defVal

      makeObservable(this, {
         value: observable,
         _timeTs: observable,
         _timeCurrTs: observable,
         active: observable,
         tick: action,
         calculated: computed
      })
   }

   tick(timeTs = null, bForce = null) {
      try {
         const val = this._callback(this)
         this._timeCurrTs = timeTs !== null ? timeTs : rootStore.time.getTimeStampTick(1)
         if (val !== this.value) {
            this.value = val
            this._timeTs = this._timeCurrTs
         }
         if (bForce === true) {
            this.i++
         }
      } catch (e) {
         console.debug("Error %o callback=%o error=%o", this._name, this._callback, e)
      }
      return this
   }

   get calculated() {
      if (this.active === false) {
         return this.value
      }
      return this._timeTs === null ? this.defVal : this.value + Math.floor((Math.max(rootStore.time.getTimeStampTick(1), this._timeCurrTs) - this._timeTs) / 1000)
   }
}

export class PlayerVjs extends PlayerAbstract {
   hasFairplaySupport = false
   hasWidevineSupport = false
   // container for Tracks
   audioTracksContainer = new PlayerTracksAudioVjs(this, "audioTracks")
   subtitleTracksContainer = new PlayerTracksSubtitleVjs(this, "subtitleTracks")
   // ref Elem for video
   _refElemContainer = null

   _api = null
   videoNode = null
   // MTVW-788, MTVW-787: idDomElement, idDomPlayer
   idDomElement = null
   idDomPlayer = null
   isLoading = false
   isStreamLoaded = null		// set in openStreamAsync (TODO rename -> ready to play)
   _isStreamLoaded = null		// set in openStreamAsync (load executed, not positioned yet)

   // poster
   isPosterVisible = false		// set in openStreamAsync, used in Layout
   hidePosterAtSeekComplete = false
   hidePosterAtPlayStarted = true

   // watchdog for restarting the player
   watchdogTimerId = null
   // TODO remove
   isStreamGrowing = null		// updated in _updateInternal (player events)
   streamLiveDelay = 0			// set in openStreamAsync
   streamCurrentDelay = 0		// set in openStreamAsync, updated in _updateInternal (player events)
   openStreamDone = false
   preloadReq = false
   // MTVW-727
   isInPip = false
   liveChannelChange = false
   nbPlayingEvents = 0
   errorCallback = null
   isDisposed = false

   constructor(parent, path) {
      super(parent, path)
      makeObservable(this, {
         isLoading: observable,
         isStreamLoaded: observable,
         _isStreamLoaded: observable,
         isPosterVisible: observable,
         isStreamGrowing: observable,
         streamLiveDelay: observable,
         streamCurrentDelay: observable,
         positionTimeStamp: observable,
         positionTimeStamp60s: observable,
         _isPaused: observable,
         _isPausedUser: observable,
         isInPip: observable,
         isDisposed: observable,
         //isInPip: computed,
         isPaused: computed,
         isPausedUser: computed,
         playbackTime: computed,
         setIsPaused: action,
         setIsPausedUser: action,
         setIsPosterVisible: action,
         setRefElemContainer: action,
         _killPlayer: action,
         _initPlayer: action,
         setIsLoading: action,
         openStreamAsync: action,
         setReject: action,
         _updateInternal: action,
         /*
         setSelectSubtitleTrack: action,
         */
         isStreamSequential: computed,
         setVolume: action,
         setMuteState: action,
         setPlay: action,
         setStop: action,
         setPause: action,
         setPosition: action,
         setPositionRelatively: action,
         setJumpLive: action,
         setPositionTimeStamp: action,
         setJumpFwd: action,
         setJumpRwd: action,
         //onFullScreenChange: action,
         //setPlayCompleted: action
         _clearWatchdog: action,
         _startWatchdog: action,
         safeWrapper: action,
         _disposeSequence: action
      })

      // register event listeners once
		this.onFullScreenChange = this.onFullScreenChange.bind(this)
		document.addEventListener('fullscreenchange', this.onFullScreenChange)
		document.addEventListener('mozfullscreenchange', this.onFullScreenChange)
		document.addEventListener('webkitfullscreenchange', this.onFullScreenChange)
		document.addEventListener('msfullscreenchange',	this.onFullScreenChange)

      if (path !== "playerAds") {
         //console.debug("VJS constructor", path)
         window.addEventListener("message", (event) => {
            if (event?.data?.dateTime && !this.extProgramDateTime) {
               //console.debug("---GOT", event?.data?.dateTime, this.__path)
               this.extProgramDateTime = event?.data?.dateTime
            }
         }, false)
      }

      isFairPlaySupported().then((res) => {
         this.hasFairplaySupport = res
      })

      isWidevineSupported().then((res) => {
         //console.debug("hasWidevineSupport", res)
         this.hasWidevineSupport = res
      })
   }

   // player information

   // initial position of stream (not event!) reported by API.getPosition after opening, DO NOT MODIFY
   // unit seconds, base value undefined (BKM = 0, Anvato = currentUnixTime() at time of opening)
   // used by _updateInternal to calculate realtime position
   initialStreamPositionApi = 0			// set in openStreamAsync (before positioning on Event), used (only) by _updateInternal to calculate realtime position if _brokenApiPlayTimeTs is not working

   // unix timestamp corresponding to initialStreamPositionApi, DO NOT MODIFY
   // unit millis, base unix time (1.1.1970)
   // used by _updateInternal to calculate realtime position
   initialStreamPositionUnix = null		// set in openStreamAsync (after positioning on Event), used (only) by _updateInternal to calculate realtime position

   streamStartPosition = null
   isNearLive = false
   extProgramDateTime = null // first #EXT-X-PROGRAM-DATE-TIME received from service worker

   // current position of stream/player reported by API.getPosition
   // unit seconds, base value undefined (BKM = 0, Anvato = currentUnixTime() at time of opening)
   currentStreamPositionApi = 0            // updated in _updateInternal, used in isJumpRwdPossibleSec, rangeTimeStampStart|End, setPositionTimeStamp

   // time that a replay source should stay behind live
   replayMargin = moment.duration(40, "seconds")

   // MTVW-700: Safari might generate sporadic / intermittent VO_OSMP_CB_PLAY_PAUSED events
   setPlayResult = false

   setErrorCallback(handler) {
      this.errorCallback = handler
   }

   // returns player vesion
   get version() {
      return videojs.VERSION
   }

   /*
   removeElementsByClass(className) {
      //var elements = document.getElementsByClassName("video-js")[0].getElementsByClassName("button")
      var elements = document.querySelectorAll(".video-js, button, .PlayerControlsVolume")
      //var elements = document.querySelectorAll(".PlayerControlsVolume")
      //var elements = document.querySelectorAll("vjs-button")
      //var elements = document.getElementById("PlayerControlsVolume")
      //elements.style.display = "flex !important"
      console.debug("removeElementsByClass", elements)
      //console.debug("parent", elements[0], elements[0].parentNode)

      //while (elements?.length > 0) {
      //   console.debug("parent", elements[0].parentNode)
      //   elements[0].parentNode.removeChild(elements[0])
      }
   }
   */

   get tech() {
      // provide any parameter to avoid warning
      return this.apiGuard?.tech?.(true)
   }

   onFullScreenChange(ev) {
      //ev.preventDefault() // not needed
      //ev.stopPropagation() // not needed
      // Firefox returns 'undefined' if not in full screen, other browsers return 'null'
      // use '!= null' instead of '!== null', see https://codedamn.com/news/javascript/check-if-undefined-null

		// MTVW-1101, finding 16, 17: requestAnimationFrame sequence to force browser reflow
		// THIS SEQUENCE IS A WORKAROUND TO AVOID SHIFTING UP THE VIDEO CONTENT WHEN SWITCHING FROM PAUSED STATE TO FULL SCREEN
		// 2x requestAnimationFrame: “wait until at least one fullscreen paint has actually happened”
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (this._api) {
					const isFs = (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement
						|| document.webkitCurrentFullScreenElement) != null
					console.debug("onFullScreenChange path %s, setting %o, isFs %o, ev %o", this.__path, this.settings.isFullScreen, isFs, ev)
					//console.debug("tech is", this, this.tech, this._api)
					runInAction(() => {
						// this is the case when ESC is pressed and the browser leaves fullscreen
						// document.fullscreenElement is null if not in full screen
						if (this.settings.isFullScreen !== isFs) this.settings.isFullScreen = isFs
					})

					if (isFs) {
						// important in paused state!
						const video = this.tech?.el()
						console.debug("video=", video)
						if (!video) return
						const w = video.style.width
						video.style.width = '99.9%'
						//video.style.display = 'none'
						// force compositor + layout rebuild, read layout-dependent property to flush pending style/layout changes immediately
						void video.offsetHeight
						// no animation frame, browser’s late compositor update reintroduces the shift
						setTimeout(() => {
							//video.style.display = ''
							video.style.width = w
						}, 900)
					}

					/* alternative, but blanks the screen
					const src = this._api?.currentSrc()
					const time = this._api?.currentTime()
					const paused = this._api?.paused()

					if (paused && isFs) {
						// Force media pipeline rebuild
						//this._api?.pause()

						// Critical: reload tech (not DOM)
						this._api?.src({ src })

						this._api?.one('loadeddata', () => {
							this._api?.currentTime(time)
							this._api?.pause().catch(() => {})
						})
					}
					*/
				}
			})
		})
      //updateCheck(false, "fullscreenchange")
   }

   //enterFullScreen(elem = null) {
   enterFullScreen = flow(function* (elem, cinemaMode) {
      //console.debug("VJS enterFullScreen %o, %o, %o", this, this._api, elem)
      //if (this._api) this._api?.enterFullscreen(elem)
      //this.videoNode.enterFullScreen()
      // NOTE VJS: video has full screen size, the height in the css could be removed, then the position changes
      // behavior is different when using element.enterFullScreen and document.xxx
      /*
      if (this._api) this._api?.requestFullscreen()
      return
      */

		//console.debug("traceFull enterFullScreen", cinemaMode)

      //MTVW-710: workaround for iPad to mitigate the progress bar sliding issue in fullscreen
      let fsElem = elem
      //const fsElem = isIpad() ? document : elem
      if (cinemaMode || isIpad() || isMobileScreenSizeWidth()) {
			document.body.classList.add("cinema-mode-open")
         fsElem = document
         //elem.style.width = "100vw"
         //elem.style.height = "100vh"
      }
		else document.body.classList.remove('cinema-mode-open')

      /*
      const fsElem = document.querySelector("video")
      console.debug("fsElem=", fsElem)
      fsElem.requestFullscreen({ navigationUI: "hide" })
      return
      */
      const reqFullScreen = fsElem.requestFullscreen || fsElem.mozRequestFullScreen ||
         fsElem.webkitRequestFullScreen || fsElem.msRequestFullscreen
      //console.debug("enterFullScreen", this.__path, cinemaMode, fsElem, reqFullScreen)

      // if fsElem === document reqFullScreen is undefined and 100% video size will be applied in Player.jsx, component PlayerBody
      if (reqFullScreen) {
         yield reqFullScreen.call(fsElem)  // no difference for this call on iPad whether elem or document is used
			setTimeout(() => {
				this._api?.addClass("VjsHost")
			}, 150)
      }

      this.isOnFullScreen()
   })

   //exitFullScreen() {
   exitFullScreen = flow(function* (elem, cinemaMode) {
      /*
      this._api?.exitFullscreen()
      return
      */
		//console.debug("traceFull exitFullScreen", cinemaMode)

		if (document.body.classList.contains('cinema-mode-open')) {
			const isNativeFs =
				document.fullscreenElement ||
				document.webkitFullscreenElement ||
				document.mozFullScreenElement ||
				document.msFullscreenElement

			if (isNativeFs) {
				const exitFs =
					document.exitFullscreen ||
					document.webkitExitFullscreen ||
					document.mozCancelFullScreen ||
					document.msExitFullscreen

				if (exitFs) {
					document.body.classList.remove('cinema-mode-open')
					return exitFs.call(document).catch((e) => {
						console.error("cinema mode exit", e)
					})
				}
			}

			document.body.classList.remove('cinema-mode-open')
			this.isOnFullScreen()
			return
		}

		// use document in any case
      elem = document
      const reqExitFullScreen = elem.exitFullscreen || elem.mozCancelFullScreen ||
         elem.webkitExitFullscreen || elem.msExitFullscreen
      console.debug("exitFullScreen", this.__path, reqExitFullScreen)
		// MTVW-1101: check if this.isOnFullScreen()
      if (reqExitFullScreen && this.isOnFullScreen()) {
			document.body.classList.remove("cinema-mode-open")
         yield reqExitFullScreen.call(elem)
			//this._api.trigger('resize')
      }

      this.isOnFullScreen()
   })

   isOnFullScreen() {
      //return this._api?.isFullscreen()
      /*
      const isFs = (document.fullscreenElement || document.mozFullScreenElement ||
         document.webkitFullscreenElement || document.msFullscreenElement) !== "undefined"
      */
      console.debug("isOnFullScreen", this.__path, this.settings.isFullScreen)
      return this.settings.isFullScreen
      //return this.settings.isFullScreenCinema
   }

   // TODO update positionTimeStamp -> currentStreamPositionUnix
   // current position of the player in unix millis
   positionTimeStamp = null	// updated in _updateInternal, used in rangeTimeStampStart|End -> PlayBar + PlayerControls
   positionTimeStamp60s = null	// updated in _updateInternal, NOTE: used to trigger _checkEventAsync via Observer (updating of event details)
   playCompleted = false
   targetApiTime = null       // set in _setPositionInternal
   durationMetaData = null

   get apiGuard() {
      //return this._api.isDisposed() ? null : this._api
      return (this._api && this._api.isDisposed()) ? null : this._api
   }

   /*
   apiWrapper = (callback) => {
      if (this.apiGuard) callback()
   }
   */

   get minimalPositionTimestamp() {
      if (this.isLiveSource) {
         return this.positionTimeStamp
      }
      const begin = (this._source?.playlist?.begin || 0)
      // MTVW-543
      //return moment(begin)
      return moment(begin).subtract(this.replayMargin)
   }

   get maximumPositionTimestamp() {
      if (this.isLiveSource) {
         return this.positionTimeStamp
      }
      const end = (this._source?.playlist?.end || 0)
      return moment.min(moment().subtract(this.replayMargin), moment(end))
   }

   // wait for sth
   _waitPlayer = new Wait(this, "_waitPlayer")
   _waitStream = new Wait(this, "_waitStream")
   _waitSeekCompleted = new Wait(this, "_waitSeekCompleted")
   _waitDisposed = new Wait(this, "_waitDisposed")
   _waitPaused = new Wait(this, "_waitPaused")
   // pause player
   _isPaused = false			// updated in player events
   _isPausedUser = false	// updated in setPlay / setPause

   get isPaused() {
      // MTVW-444: paused state for displaying pause icon
      if (this.__parent.playerFg) return true
      return this._isPaused || this._isPausedUser
   }

   get isPausedUser() {
      return this._isPausedUser
   }

   get isPlayerActive() {
      // MTVW-588: added && !this.playCompleted
      //console.debug("isPlayerActive", this.__path, this._refElemContainer !== null, !this.playCompleted)
      return this._refElemContainer !== null && !this.playCompleted
   }
   // check interval on pause
   _intervalId = null
   get isLiveSource() {
      return ("live" === this._source?.playlist?.streamType) || false
   }

   get isAdSource() {
      return ("ad" === this._source?.playlist?.streamType) || false
   }
   get playbackTime() {
      //console.debug("playbackTime", this._api?.currentTime(), this.currentStreamPositionApi - this.initialStreamPositionApi)
      return this.apiGuard?.currentTime() || 0
   }
   get _isBrokenLatency() {
      return isSafari()
   }
   get _isBrokenPositioning() {
      // MTV-2967: Special handling with VO player V3.33 and V3.34 for
      // Safari on Mac and iOS and browsers on iPad, with Safari 15 it's not broken like before
      return isSafari() && safariMainVersion() < 15
   }
   // real api from player
   rangeStartStruct = new PlayerTimeVal(
      "rangeStart",
      timeVal => {
         timeVal.active = !!this.isStreamGrowing
         //const buf = this._api?.buffered()
         //console.debug("rangeStartStruct", buf, this._api?.duration(), buf.start(0), buf.end(0))
         return this._isStreamLoaded ? helperFixNaN(parseFloat(this._api?.buffered().start(0))) : 0
      },
      0
   )

   rangeEndStruct = new PlayerTimeVal(
      "rangeEnd",
      timeVal => {
         timeVal.active = !!this.isStreamGrowing
         if (!this._isStreamLoaded) {
            return 0
         }
         // buffered does not return the entire time range, just what is buffered so far
         const buf = this._api?.buffered()
         //console.debug("rangeEnd", buf, this._api?.duration(), buf.start(0), buf.end(0))
         //let { start, end } = this._api?.buffered()
         let start = 0
         //let end = (this._source.playlist.end - this._source.playlist.start) / 1000
         let end = this._api?.duration()
         if (this.isLiveSource) {
            start = this._api?.buffered().start(0)
            //console.debug("buffered", this._api?.buffered().start(0), this._api?.buffered().end(0))
            //end = this._api?.duration()
            end = this._api?.buffered().end(0)
            //end = (moment().utc().valueOf() - this.initialStreamPositionUnix) / 1000 + 40
         }
         else if (this.isNearLive) {
            const now = moment.utc().valueOf()
            //console.debug("isNearLive", this._source.playlist, now, this._source.playlist.begin, (now - this._source.playlist.begin) / 1000)
            end = (now - this._source.playlist.begin) / 1000
            //this.initialStreamPositionApi = 42
         }
         start = helperFixNaN(parseFloat(start), 0)
         end = helperFixNaN(parseFloat(end), 0)
         // case start: 140.04, end: 27
         // case end position lower than currently played
         // TODO joachim: when is start > end?
         //if (end === Infinity) end = moment().subtract(moment.duration(40, "seconds")).utc().valueOf() - this.initialStreamPositionUnix
         //console.debug("rangeEndStruct", start, end, Math.max(start > end ? end + start : end, this.currentStreamPositionApi))
         return Math.max(start > end ? end + start : end, this.currentStreamPositionApi)
      },
      0
   )


   _tryCatcher = (callback, defVal = null, checkIsLoaded = true) => {
      if (checkIsLoaded === true && !this._isStreamLoaded) {
         return defVal
      }
      try {
         return callback()
      } catch (e) {
         console.error("Vjs error (callback=%o,defVal=%o) exception=%o", callback, defVal, e)
         return defVal
      }
   }

   safeWrapper = (callback, info) => {
      setTimeout(() => {
         runInAction(() => {
            try {
               callback()
            }
            catch (err) {
               console.error("CAUGHT in safeWrapper: %s, %o", info, err)
            }
         })
      })
   }

   // start section vo api
   // returns current position (in seconds)
   _apiGetPosition(defVal = 0) {
      //console.debug("_apiGetPosition %s", this._api?.getPosition())
      return this._tryCatcher(() => parseFloat(this._api?.currentTime()), defVal)
   }
   _apiGetDuration(defVal = 0) {
      //console.debug("getDuration %s", this._api?.getDuration())
      return this._tryCatcher(() => parseFloat(this._api?.getDuration()), defVal)
   }

   _apiGetBufferDuration(defVal = 0) {
      // TODO VJS
      return this._tryCatcher(() => helperFixNaN(Math.min(parseFloat(this._api?.getValidBufferDuration("video")), parseFloat(this._api?.getValidBufferDuration("audio"))), defVal))
   }
   _apiGetCurrentLiveLatency() {
      // not supported by videojs
      return 0
      /*
      // MTV-1345, MTV-1780, MTV-1783: Workaround for wrong value on Safari
      //return this._tryCatcher(() => helperFixNaN(parseFloat(this._api?.getCurrentLiveLatency())))
      const latency = this._tryCatcher(() => helperFixNaN(parseFloat(this._api?.getCurrentLiveLatency())))
      // MTV-2967: replace condition with _isBrokenPositioning
      //if (latency > 1000) {
      //console.debug("LATENCY %s", latency)
      if (this._isBrokenLatency) {
         // for Safari, attempt to synchronize end of movie with update of EPG
         return 6
      }
      else {
         return latency
      }
      */
   }

   _apiIsLive() {
      //console.warn("_apiIsLive", this._api ? this._tryCatcher(() => this._api?.isLive()) : false)
      // TODO VJS
      //return this._api ? this._tryCatcher(() => this._api?.isLive()) : false
      return this.isLiveSource
   }

   // ATTN: this method reports the wrong value for timeshifted Anvato streams (and for MySports replay)
   _brokenApiPlayTimeTs() {
      // MTV-1345, MTV-1780, MTV-1783: Workaround for bug in _brokenApiPlayTimeTs on Safari
      // -> same result as api.getCurrentTimeForLocal() on Chrome(Windows)
      //return this._tryCatcher(() => moment(this._api?.getCurrentTimeForLocal()).valueOf())
      const latencyMs = this._apiGetCurrentLiveLatency() * 1000
      const fixedTime = rootStore.time.getTimeStamp() + 2000 - latencyMs
      /*
      const apiTime = this._tryCatcher(() => moment(this._api?.getCurrentTimeForLocal()).valueOf())
      console.debug("api time %s fixed time %s diff %s latency %s", apiTime, fixedTime, fixedTime - apiTime, latencyMs)
      */
      return fixedTime
   }
   // jump to a defined position in the stream (unix time milliseconds)
   // MTVW-543
   _setPositionInternal(millis) {
      // MTVW-217 / MTVW-127 / MTVW-72: try / catch, test for NaN
      try {
         console.debug("set(millis=%o)", millis, moment(millis).utc().format(TIME_FORMAT_BACKEND))
         this.clearSubtitles()
         if (!this.initialStreamPositionUnix) return this
         // REMARK for BKM streams setting position is possible, for Anvato streams NOT (url parameter)
         const now = moment()
         const millisLimited = Math.min(millis, now.clone().subtract(this.replayMargin).valueOf())
         //const millisLimited = millis
         console.debug("millisLimited %s (%s), pValueOf %s (%s), pApi %s", millisLimited, moment(millisLimited).utc().format(TIME_FORMAT_BACKEND), this.initialStreamPositionUnix, moment(this.initialStreamPositionUnix).utc().format(TIME_FORMAT_BACKEND), this.initialStreamPositionApi)
         const apiPos = millisLimited - (this.initialStreamPositionUnix) + this.initialStreamPositionApi * 1000
         //console.debug("pos Api=%o valueOf=%o  initialStreamPosition Api=%o valueOf=%o", apiPos, moment(millis).utc().format(TIME_FORMAT_BACKEND), this.initialStreamPositionApi * 1000, moment(this.initialStreamPositionUnix).utc().format(TIME_FORMAT_BACKEND))
         console.debug("set(%s, pos=%o, isNaN %s, millis %s, limited %s, unix %s, api %s)", this.__path, apiPos / 1000, isNaN(apiPos), millis, millisLimited, this.initialStreamPositionUnix, this.initialStreamPositionApi)
         if (!isNaN(apiPos)) {
            // MTVW-816
            // MTVW-864: don't send the event of skipping or seeking in the replay/recording stream.
            // this is covered by the keep alive interval play call
            //if (this.openStreamDone) rootStore.page.CoCoBookmarks.playbackSeek(this, millisLimited)
            this.targetApiTime = apiPos / 1000
            // TODO VJS: recheck
            //this._waitSeekCompleted.setReset()
            this._api?.currentTime(this.targetApiTime)
         }
      }
      catch (err) {
         console.debug("caught %o", err)
      }
      return this
   }

   // end section vjs api

   // MTVW-444: added force
   setIsPaused(value, force = false) {
      //console.debug("setIsPaused", this.__path, value, force)
      //if (force) this.apiWrapper(() => { this._api.pause() })
      if (force) this.apiGuard?.pause()
      this._isPaused = value
   }

   setIsPausedUser(value) {
      this._isPausedUser = value
   }


   posterTimeoutId = null
   setIsPosterVisible(visible, info, mute = false, delay = 0) {
      console.debug("setIsPosterVisible %s, caller %s, visible %s, mute %s, delay %s", this.__path, info, visible, mute, delay)
      try {
         if (mute) this._api?.muted(true)
      }
      catch (e) {
      }
      if (this.posterTimeoutId) clearTimeout(this.posterTimeoutId)
      this.posterTimeoutId = setTimeout(() => {
         runInAction(() => { this.isPosterVisible = visible })
      }, delay)
      //console.debug("posterVisible %s", this.isPosterVisible)
   }

   setRefElemContainer = flow(function* (refElem, doDispose = false) {
      console.debug("setRefElemContainer player %s, ref %o, refContainer %o, dispose %s", this.__path, refElem, this._refElemContainer, doDispose)
		// MTVW-788, MTVW-787: idDomElement. idDomPlayer
      if (refElem) {
         this.idDomElement = refElem.id
         this.idDomPlayer = refElem.firstChild?.firstChild?.id?.replace("vjs_", "").replace("_html5_api", "")
         console.debug("idDomElement %s, idDomPlayer %s, firstChild %o", this.idDomElement, this.idDomPlayer, refElem?.firstChild?.firstChild)
		}
      try {
         // remove player instance
         /*
         if (this.keepRefElem && (this.__path === "playerLiveTvVo" || this.__path === "playerDetailPageVo")) {
            //this.keepRefElem = false
            //this.setIsPosterVisible(false)
            return
         }
         */
         if (!refElem && this._refElemContainer !== null) {
            console.debug("setRefElemContainer 1", this.__path, doDispose, document.getElementById("tvPlayer") /*, this._api?.readyState?.()*/)
            // Stops the player if refElem is null (e.g. when Live TV tab is left)

            // MTVW-217: Save current Live TV state - DISABLED
            //if (this.__path === "playerLiveTvVo") this._isInactive("reasonStopped")
            // MTVW-217: unpause
            if (this._isPausedUser) this.setIsPausedUser(false)

            // MTVW-727b: canResetPlayer condition, prevent player stop when changing tabs in the app
            if (this.canResetPlayer()) {
               console.info("@(refElem=%o) ._refElemContainer=%o -> remove player container", refElem, this._refElemContainer)
               console.debug("setRefElemContainer 1 %s executing", this.__path)
               //this._api = null   // don't do
               yield this._killPlayer(doDispose)
               this._refElemContainer = null
               /* causes execptions when switching between Discover and LiveTV
               this._waitPlayer.setReject("IGNORE")
               this._waitStream.setReject("IGNORE")
               this._waitSeekCompleted.setReject("IGNORE")
               */
               this._clearWatchdog()
            }
         }
         // create player instance new
         else if (refElem && this._refElemContainer === null) {
            console.debug("setRefElemContainer 2", this.__path, refElem /*, this._api?.readyState?.()*/)
            console.info("@(refElem=%o) -> create  a new player container", refElem)
            this._waitPlayer.setReset()
            this._waitStream.setReset()
            this._waitSeekCompleted.setReset()
            //yield this._killPlayer(doDispose)
            this._refElemContainer = refElem
            //if (isIphone()) this._initPlayer() // autoplay not allowed if commented
            //this._api?.reset()
            //this._waitPlayer.setResolve()
            this._clearWatchdog()
         }
         // replace player instance
         else if (refElem !== this._refElemContainer) {
            // MTVW-727b: canResetPlayer condition, prevent player stop when changing tabs in the app
            console.debug("setRefElemContainer 3", this.__path)
            if (this.canResetPlayer() /*|| this.__path === "playerLiveTvVo"*/) {
               console.debug("setRefElemContainer 3 %s executing", this.__path)
               console.info("@(refElem=%o) ._refElemContainer=%o -> replace player container", refElem, this._refElemContainer)
               this._waitPlayer.setReset()
               this._waitStream.setReset()
               this._waitSeekCompleted.setReset()
               yield this._killPlayer(doDispose)
               this._refElemContainer = refElem
               //if (isIphone()) this._initPlayer() // not really needed
               //this._api?.reset()
               this._waitPlayer.setResolve()
               this._clearWatchdog()
            }
         }
         else {
            console.debug("setRefElemContainer 4", this.__path, doDispose, refElem, this._refElemContainer)
            //this._refElemContainer = refElem
            // MTVW-727b: canResetPlayer condition, prevent player stop when changing tabs in the app
            console.debug("setRefElemContainer 4", this.__path)
            if (this.canResetPlayer()) {
               console.debug("setRefElemContainer 4 %s executing", this.__path)
               // MTVW-996
               if (this.__path === "playerDiscover") yield this._killPlayer(true)
               else yield this._disposeSequence(doDispose)
            }
         }
      }
      catch (err) {
         console.error("CAUGHT in setRefElemContainer", err)
      }
      return this
      console.debug("setRefElemContainer", refElem?.id, refElem, this.__path)
      //if (this.__path === "playerLiveTvVo") {
      //if (refElem.id === "tvPlayer") {
      videojs.options.controls = false

      this._waitPlayer.setReset()
      this._waitStream.setReset()
      this._waitSeekCompleted.setReset()
      // TODO VJS: not in any case, calls interactionEvent that deactivates the ads player
      //this._killPlayer()
      this._refElemContainer = refElem
      this._initPlayer()
      // this._waitPlayer.setResolve() set in onPlayerReady
      //this._waitPlayer.setResolve()
      this._clearWatchdog()
      //}
      return this
   })

   _disposeSequence = flow(function* (doDispose = true) {
      //const tinyVideo = "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAr9tZGF0AAACoAYF//+c3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDEyNSAtIEguMjY0L01QRUctNCBBVkMgY29kZWMgLSBDb3B5bGVmdCAyMDAzLTIwMTIgLSBodHRwOi8vd3d3LnZpZGVvbGFuLm9yZy94MjY0Lmh0bWwgLSBvcHRpb25zOiBjYWJhYz0xIHJlZj0zIGRlYmxvY2s9MTowOjAgYW5hbHlzZT0weDM6MHgxMTMgbWU9aGV4IHN1Ym1lPTcgcHN5PTEgcHN5X3JkPTEuMDA6MC4wMCBtaXhlZF9yZWY9MSBtZV9yYW5nZT0xNiBjaHJvbWFfbWU9MSB0cmVsbGlzPTEgOHg4ZGN0PTEgY3FtPTAgZGVhZHpvbmU9MjEsMTEgZmFzdF9wc2tpcD0xIGNocm9tYV9xcF9vZmZzZXQ9LTIgdGhyZWFkcz02IGxvb2thaGVhZF90aHJlYWRzPTEgc2xpY2VkX3RocmVhZHM9MCBucj0wIGRlY2ltYXRlPTEgaW50ZXJsYWNlZD0wIGJsdXJheV9jb21wYXQ9MCBjb25zdHJhaW5lZF9pbnRyYT0wIGJmcmFtZXM9MyBiX3B5cmFtaWQ9MiBiX2FkYXB0PTEgYl9iaWFzPTAgZGlyZWN0PTEgd2VpZ2h0Yj0xIG9wZW5fZ29wPTAgd2VpZ2h0cD0yIGtleWludD0yNTAga2V5aW50X21pbj0yNCBzY2VuZWN1dD00MCBpbnRyYV9yZWZyZXNoPTAgcmNfbG9va2FoZWFkPTQwIHJjPWNyZiBtYnRyZWU9MSBjcmY9MjMuMCBxY29tcD0wLjYwIHFwbWluPTAgcXBtYXg9NjkgcXBzdGVwPTQgaXBfcmF0aW89MS40MCBhcT0xOjEuMDAAgAAAAA9liIQAV/0TAAYdeBTXzg8AAALvbW9vdgAAAGxtdmhkAAAAAAAAAAAAAAAAAAAD6AAAACoAAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAhl0cmFrAAAAXHRraGQAAAAPAAAAAAAAAAAAAAABAAAAAAAAACoAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAgAAAAIAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAAAqAAAAAAABAAAAAAGRbWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAAwAAAAAgBVxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAABPG1pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAPxzdGJsAAAAmHN0c2QAAAAAAAAAAQAAAIhhdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAgACABIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAAMmF2Y0MBZAAK/+EAGWdkAAqs2V+WXAWyAAADAAIAAAMAYB4kSywBAAZo6+PLIsAAAAAYc3R0cwAAAAAAAAABAAAAAQAAAgAAAAAcc3RzYwAAAAAAAAABAAAAAQAAAAEAAAABAAAAFHN0c3oAAAAAAAACtwAAAAEAAAAUc3RjbwAAAAAAAAABAAAAMAAAAGJ1ZHRhAAAAWm1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAG1kaXJhcHBsAAAAAAAAAAAAAAAALWlsc3QAAAAlqXRvbwAAAB1kYXRhAAAAAQAAAABMYXZmNTQuNjMuMTA0"
      //const tinyVideo = "data:AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAr9tZGF0AAACoAYF//+c3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDEyNSAtIEguMjY0L01QRUctNCBBVkMgY29kZWMgLSBDb3B5bGVmdCAyMDAzLTIwMTIgLSBodHRwOi8vd3d3LnZpZGVvbGFuLm9yZy94MjY0Lmh0bWwgLSBvcHRpb25zOiBjYWJhYz0xIHJlZj0zIGRlYmxvY2s9MTowOjAgYW5hbHlzZT0weDM6MHgxMTMgbWU9aGV4IHN1Ym1lPTcgcHN5PTEgcHN5X3JkPTEuMDA6MC4wMCBtaXhlZF9yZWY9MSBtZV9yYW5nZT0xNiBjaHJvbWFfbWU9MSB0cmVsbGlzPTEgOHg4ZGN0PTEgY3FtPTAgZGVhZHpvbmU9MjEsMTEgZmFzdF9wc2tpcD0xIGNocm9tYV9xcF9vZmZzZXQ9LTIgdGhyZWFkcz02IGxvb2thaGVhZF90aHJlYWRzPTEgc2xpY2VkX3RocmVhZHM9MCBucj0wIGRlY2ltYXRlPTEgaW50ZXJsYWNlZD0wIGJsdXJheV9jb21wYXQ9MCBjb25zdHJhaW5lZF9pbnRyYT0wIGJmcmFtZXM9MyBiX3B5cmFtaWQ9MiBiX2FkYXB0PTEgYl9iaWFzPTAgZGlyZWN0PTEgd2VpZ2h0Yj0xIG9wZW5fZ29wPTAgd2VpZ2h0cD0yIGtleWludD0yNTAga2V5aW50X21pbj0yNCBzY2VuZWN1dD00MCBpbnRyYV9yZWZyZXNoPTAgcmNfbG9va2FoZWFkPTQwIHJjPWNyZiBtYnRyZWU9MSBjcmY9MjMuMCBxY29tcD0wLjYwIHFwbWluPTAgcXBtYXg9NjkgcXBzdGVwPTQgaXBfcmF0aW89MS40MCBhcT0xOjEuMDAAgAAAAA9liIQAV/0TAAYdeBTXzg8AAALvbW9vdgAAAGxtdmhkAAAAAAAAAAAAAAAAAAAD6AAAACoAAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAhl0cmFrAAAAXHRraGQAAAAPAAAAAAAAAAAAAAABAAAAAAAAACoAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAgAAAAIAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAAAqAAAAAAABAAAAAAGRbWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAAwAAAAAgBVxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAABPG1pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAPxzdGJsAAAAmHN0c2QAAAAAAAAAAQAAAIhhdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAgACABIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAAMmF2Y0MBZAAK/+EAGWdkAAqs2V+WXAWyAAADAAIAAAMAYB4kSywBAAZo6+PLIsAAAAAYc3R0cwAAAAAAAAABAAAAAQAAAgAAAAAcc3RzYwAAAAAAAAABAAAAAQAAAAEAAAABAAAAFHN0c3oAAAAAAAACtwAAAAEAAAAUc3RjbwAAAAAAAAABAAAAMAAAAGJ1ZHRhAAAAWm1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAG1kaXJhcHBsAAAAAAAAAAAAAAAALWlsc3QAAAAlqXRvbwAAAB1kYXRhAAAAAQAAAABMYXZmNTQuNjMuMTA0"
      const tinyVideo = "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAu1tZGF0AAACrQYF/ / +p3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE1NSByMjkwMSA3ZDBmZjIyIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxOCAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTMgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTEgc2NlbmVjdXQ9NDAgaW50cmFfcmVmcmVzaD0wIHJjX2xvb2thaGVhZD00MCByYz1jcmYgbWJ0cmVlPTEgY3JmPTI4LjAgcWNvbXA9MC42MCBxcG1pbj0wIHFwbWF4PTY5IHFwc3RlcD00IGlwX3JhdGlvPTEuNDAgYXE9MToxLjAwAIAAAAAwZYiEAD//8m+P5OXfBeLGOfKE3xkODvFZuBflHv/+VwJIta6cbpIo4ABLoKBaYTkTAAAC7m1vb3YAAABsbXZoZAAAAAAAAAAAAAAAAAAAA+gAAAPoAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAIYdHJhawAAAFx0a2hkAAAAAwAAAAAAAAAAAAAAAQAAAAAAAAPoAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAACgAAAAWgAAAAAAJGVkdHMAAAAcZWxzdAAAAAAAAAABAAAD6AAAAAAAAQAAAAABkG1kaWEAAAAgbWRoZAAAAAAAAAAAAAAAAAAAQAAAAEAAVcQAAAAAAC1oZGxyAAAAAAAAAAB2aWRlAAAAAAAAAAAAAAAAVmlkZW9IYW5kbGVyAAAAATttaW5mAAAAFHZtaGQAAAABAAAAAAAAAAAAAAAkZGluZgAAABxkcmVmAAAAAAAAAAEAAAAMdXJsIAAAAAEAAAD7c3RibAAAAJdzdHNkAAAAAAAAAAEAAACHYXZjMQAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAACgAFoASAAAAEgAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABj//wAAADFhdmNDAWQACv/hABhnZAAKrNlCjfkhAAADAAEAAAMAAg8SJZYBAAZo6+JLIsAAAAAYc3R0cwAAAAAAAAABAAAAAQAAQAAAAAAcc3RzYwAAAAAAAAABAAAAAQAAAAEAAAABAAAAFHN0c3oAAAAAAAAC5QAAAAEAAAAUc3RjbwAAAAAAAAABAAAAMAAAAGJ1ZHRhAAAAWm1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAG1kaXJhcHBsAAAAAAAAAAAAAAAALWlsc3QAAAAlqXRvbwAAAB1kYXRhAAAAAQAAAABMYXZmNTguMTIuMTAw"

      this._waitDisposed.setReset()
      if (doDispose) {
         if (this._api && !this._api?.isDisposed()) {
            //this._api?.currentTime(0)
            console.debug("disposing 1", this.__path, this._api?.readyState?.(), this._api?.isDisposed(), this._refElemContainer, document.getElementById("tvPlayer"))

            //this._api?.reset()
            // wait for pause event???
            //vjs_playerLiveTvVo_html5_api
            /*
            if (!this._api?.paused()) {
               this._waitPaused.setReset()
               this._api?.pause()
               yield this._waitPaused.waitAsync()
               console.debug("_waitPaused succeeded")
            }
            */

            /*
            yield this._api?.src({
               src: tinyVideo,
               type: "video/mp4"
            })
            */

            //if (this._api?.readyState?.() > 0) {
            //videojs('vjs_' + this.__path).dispose()
            this._api?.dispose()
            try {
               yield this._waitDisposed.waitAsync()
            }
            catch (e) {
               console.warn("CAUGHT _waitDisposed", e)
            }
            //}
            //else console.debug("readyState 0")
            console.debug("creating disposedEvent")
            console.debug("disposed", this.__path, this._api?.isDisposed(), this._refElemContainer, document.getElementById("tvPlayer"))
         }
         this.isDisposed = true
         EventEmitter.dispatch("disposedEvent", this.__path)
      }
   })

   _killPlayer = flow(function* (doDispose = true) {
      console.debug("_killPlayer %s, canReset %s", this.__path, this.canResetPlayer())
      try {
         //if (!this._api?.isDisposed() /*&& this._source*/) this._api?.pause()
         //if (!this._api?.isDisposed() && !doDispose) this._api?.pause()

         //if (!this.source) return
         //console.debug("_killPlayer %o", this._source)
         // MTVW-514: clear time stamps and kill player after interactionEvent, this.positionTimeStamp parameter
         // MTVW-531, MTVW-529: Direct execution within player callback may cause impredicable behavior on Safari

         // CAUSES problems if dispatched
         //setTimeout(() => {
         // GT12
         const position = this.positionTimeStamp
         try {
            //runInAction(() => {
            if (this._intervalId !== null) {
               clearInterval(this._intervalId)
               this._intervalId = null
            }
            /*
            this.isStreamLoaded = false
            this._isStreamLoaded = false
            */
            /*
            this._api?.close()
            this._apiEvents.forEach(param => {
               this._api?.removeEventListener(...param)
            })
            this._apiEvents = []
            */
            /*
            this.setIsLoading(false)
            this.positionTimeStamp = null
            this.positionTimeStamp60s = null
            */

            // MTVW-727b: canResetPlayer condition, prevent player stop when changing tabs in the app
            if (this.canResetPlayer() /*|| this.__path === "playerLiveTvVo"*/ /*&& rootStore.page.OverlayEventDetails.callingContext !== "Search"*/) {
               console.debug("dispose=", this.__path, this.idDomPlayer, this.__parent.pipPlayer?.__path, rootStore.page.OverlayEventDetails.callingContext)
               // MTVW-444: this.__path !== "playerBg"
               // TODO: check
               if (!this.__parent.isInLiveTv && this.__path !== "playerBg") {
                  this.showPiP(false)
                  this.isInPip = false
                  //this.__parent.setPiPPlayer(null)
               }
               // remove vjs event handlers
               try {
                  yield this._disposeSequence(doDispose)
               }
               catch (e) {
                  console.warn("CAUGHT _killPlayer 2", e)
               }
               this.isStreamLoaded = false
               this._isStreamLoaded = false
               this.setIsLoading(false, "_killPlayer")
               this.positionTimeStamp = null
               this.positionTimeStamp60s = null
            }

            if (this._source?.playlist && this._source?.type !== SourceTypeList.AdPlaylist) {
               yield this._source.playlist.stopKeepAlive()
            }

            //this._api = null
            //if (!isIphone()) this._api?.pause() // autoplay issue on iPhone
            //this._api?.reset()
         }
         //this.setSource(null)
         //})
         catch (e) {
            console.error("CAUGHT _killPlayer 3", e)
         }
         //})
         try {
            yield this._source?.interactionEvent?.(doDispose ? "destroy" : "changeAsset", this, position)
         }
         catch (e) {
            console.warn("CAUGHT _killPlayer 1", e)
         }
         return

         if (this._intervalId !== null) {
            clearInterval(this._intervalId)
            this._intervalId = null
         }
         if (this._source?.playlist && this._source?.type !== SourceTypeList.AdPlaylist) {
            yield this._source.playlist.stopKeepAlive()
         }
         this.isStreamLoaded = false
         this._isStreamLoaded = false
         /*
         this._api?.close()
         this._apiEvents.forEach(param => {
            this._api?.removeEventListener(...param)
         })
         this._apiEvents = []
         */
         this.setIsLoading(false)
         this.positionTimeStamp = null
         this.positionTimeStamp60s = null
         // remove vjs event handlers
         if (!this._api?.isDisposed()) this._api?.dispose()
      }
      catch (err) {
         console.debug("CAUGHT in _killPlayer", err)
      }
   })

   isFairplay() {
      //console.debug("isFairplay", this.__path, this.hasFairplaySupport, this._source?.getDrmScheme)
      return this.hasFairplaySupport && this._source?.getDrmScheme === "fairplay"
   }
   isWidevine() {
      //console.debug("isWidevine", this.__path, this.hasWidevineSupport, this._source?.getDrmScheme)
      return this.hasWidevineSupport && this._source?.getDrmScheme === "widevine"
   }

	getStreamUrl() {
		// MTVW-1119: override stream URL for testing
		const overrideSrc = getUrlQueryParam("streamUrl")?.replace("http://", "https://")
		const streamUrl = overrideSrc ?? this._source?.getStreamUrl
		return streamUrl
	}

	isDash() {
		return this.getStreamUrl()?.includes(".mpd")
	}

   _configureDrm() {
      console.debug(timeNow() + " VJS EVENT: _configureDrm")
      const arrayToString = function (array) {
         var uint16array = new Uint16Array(array.buffer)
         return String.fromCharCode.apply(null, uint16array)
      }

      //var base64EncodeUint8Array = function (a) { for (var d, e, f, g, h, i, j, b = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=", c = "", k = 0; k < a?.length;)d = a[k++], e = k < a?.length ? a[k++] : Number.NaN, f = k < a?.length ? a[k++] : Number.NaN, g = d >> 2, h = (3 & d) << 4 | e >> 4, i = (15 & e) << 2 | f >> 6, j = 63 & f, isNaN(e) ? i = j = 64 : isNaN(f) && (j = 64), c += b.charAt(g) + b.charAt(h) + b.charAt(i) + b.charAt(j); return c }
      function toHexString(byteArray) {
         return Array.from(byteArray, function (byte) {
            return ('0' + (byte & 0xFF).toString(16)).slice(-2)
         }).join('')
      }

      const base64EncodeUint8Array = function (input) {
         var keyStr = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/="
         var output = ""
         var chr1, chr2, chr3, enc1, enc2, enc3, enc4
         var i = 0
         while (i < input?.length) {
            chr1 = input[i++]
            chr2 = i < input?.length ? input[i++] : Number.NaN // Not sure if the index
            chr3 = i < input?.length ? input[i++] : Number.NaN // checks are needed here

            enc1 = chr1 >> 2
            enc2 = ((chr1 & 3) << 4) | (chr2 >> 4)
            enc3 = ((chr2 & 15) << 2) | (chr3 >> 6)
            enc4 = chr3 & 63

            if (isNaN(chr2)) {
               enc3 = enc4 = 64
            } else if (isNaN(chr3)) {
               enc4 = 64
            }
            output += keyStr.charAt(enc1) + keyStr.charAt(enc2) +
               keyStr.charAt(enc3) + keyStr.charAt(enc4)
         }
         return output
      }

      const base64DecodeUint8Array = function (input) {
         var raw = window.atob(input)
         var rawLength = raw?.length
         var array = new Uint8Array(new ArrayBuffer(rawLength))

         for (var i = 0; i < rawLength; i++)
            array[i] = raw.charCodeAt(i)

         return array
      }

      const checkLicenseError = function (statusCode) {
         let error = null
         if (statusCode === 403) {
            error = new GenericError(l10n.featurePlayerErrorNoPlaybackTitle,
               l10n.featurePlayerErrorDrm403Message.replaceAll("\\n", "\n"))
         }
         else if (statusCode === 500) {
            error = new GenericError(l10n.featurePlayerErrorNoPlaybackTitle,
               l10n.featurePlayerErrorDrm500Message.replaceAll("\\n", "\n"))
         }
         return error
      }

      let keySystems = {}
      console.debug("_configureDrm tech %o, fairplay %s, widevine %s, scheme %s", this.tech, this.isFairplay(), this.isWidevine(), this._source?.getDrmScheme)
      if ((this.isFairplay() || this.isWidevine())) {
         if (this.tech) {
            this.tech.on('licenserequestattempted', function (event) {
               // Act on event
               console.debug("eme: licenserequestattempted", event)
            })
            this.tech.on('keystatuschange', function (event) {
               // Event data:
               // keyId
               // status: usable, output-restricted, etc
               // target: the MediaKeySession object that caused this event
               console.debug("eme: keystatuschange", event)
            })
            this.tech.on('keysessioncreated', function (event) {
               // note that there is no event data for keysessioncreated
               console.debug("eme: keysessioncreated", event)
            })
         }

         const certUrl = this._source.getStreamUrlCertificate
         const licenseUrl = this._source.getStreamUrlLicense
         // MTVW-1105: additional output for bdd log
         console.warn("licenseUrl %s, deviceId %s", licenseUrl, getQlHeaders()?.['X-Quickline-Deviceid'])

         let drmDeviceId = ""

         /* simplest approach, not working
         keySystems = {
            'com.apple.fps.1_0': {
               certificateUri: this._source.getStreamUrlCertificate,
               licenseUri: this._source.getStreamUrlLicense,
               licenseHeaders: {
                  'Content-Type': 'application/x-www-form-urlencoded'
               }
            }
         }
         */

         // IMPORTANT: since videojs-contrib-eme 5.1.2, widevine enrypted events are not handled if 'com.apple.fps.1_0' and 
         // 'com.widevine.alpha' are both listed in keySystems:
         // see also https://experienceleague.adobe.com/docs/primetime/programming/browser-tvsdk-2-4/content-protection/c-psdk-browser-tvsdk-2.4-impl-drm-callbacks.html?lang=en
         if (this.isFairplay()) {
            keySystems = {
               'com.apple.fps.1_0': {
                  //certificateUri: certUrl,
                  //licenseUri: licenseUrl,
                  getCertificate: function (emeOptions, callback) {
                     console.debug(timeNow() + " VJS EVENT: fairplay getCertificate", certUrl, emeOptions)
                     videojs.xhr({
                        url: certUrl,
                        /*
                        headers: {
                           'Pragma': 'no-cache',
                           'Cache-Control': 'no-cache',
                           //'Content-Type': 'text/xml; charset=utf-8'
                        },
                        */
                        headers: getQlHeaders(),
                        responseType: 'arraybuffer',
                        method: 'GET',
                     }, function (err, response, responseBody) {
                        if (err) {
                           console.error("getCertificate error", err)
                           callback(err)
                           return
                        }
                        //callback(null, base64DecodeUint8Array(responseBody))
                        const arr = new Uint8Array(responseBody)
                        console.debug(timeNow() + " VJS EVENT: fairplay getCertificate callback", responseBody, arr)
                        callback(null, arr)
                        /*
                         console.debug("getCertificate callback responseBody", responseBody)
                         var certText = responseBody.trim()
                         certText = certText.replace(/^"(.+(?="$))"$/, '$1')
                         console.debug("getCertificate callback certText", certText)
                         //return base64DecodeUint8Array(certText)
                         callback(null, new Uint8Array(certText))
                         */
                     })
                  },
                  getContentId: function (emeOptions, initData) {
                     // videojs-contrib-eme 4.0.0: getContentId will now receive a string representation of the initData
                     //const initString = arrayToString(initData)
                     const initString = initData
                     console.debug(timeNow() + " VJS EVENT: fairplay getContentId", initData, initString)
                     //const contentId = initString.substring(initData.indexOf('skd://') + 9)
                     //const contentId = initData.substring(initData.indexOf('skd://') + 6)
                     const contentId = initString
                     const tokens = contentId ? contentId.split('/') : []
                     if (tokens?.length) {
                        console.debug("getContentId", tokens[tokens?.length - 1], initData)
                        return tokens[tokens?.length - 1]
                     } else {
                        console.debug("getContentId no tokens")
                        return ''
                     }
                     console.debug("getContentId", contentId, initData)
                     return contentId
                  },
                  getLicense: function (emeOptions, contentId, keyMessage, callback) {
                     console.debug(timeNow() + " VJS EVENT: fairPlay fetch key", licenseUrl, contentId, keyMessage, emeOptions/*, toHexString(keyMessage)*/)
                     const body = {
                        key_id: contentId,
                        spc: base64EncodeUint8Array(keyMessage),
                        casting: false,
                        drm_device_id: drmDeviceId
                     }
                     const jsonBody = JSON.stringify(body)
                     videojs.xhr({
                        url: licenseUrl,
                        method: 'POST',
                        responseType: 'application/json',
                        body: jsonBody,
                        /*
                        headers: {
                           'Content-type': 'application/x-www-form-urlencoded',
                        }
                        */
                        headers: getQlHeaders()
                     }, function (err, response, responseBody) {
                        // test purpose
                        //response.statusCode = 403
                        // MTVW-927: check for 403 and 500
                        const error = checkLicenseError(response.statusCode)
                        console.debug(timeNow() + " VJS EVENT: fairplay response license %s, err %o, rsp %o, body %o", response.statusCode, err, response, responseBody)
                        if (error) {
                           if (!rootStore.page.Player.error) rootStore.page.Player.error = error
                           return
                        }
                        if (err) {
                           console.error("getLicense error", err, response, responseBody)
                           callback(err)
                           return
                        }
                        const body = JSON.parse(responseBody)
                        //const result = body?.ckc
                        const result = new Uint8Array(base64DecodeUint8Array(body?.ckc))
                        drmDeviceId = body?.drm_device_id
                        console.debug("fetch key callback err %o, body %o, result %o, resp %o, drmDeviceId %o", err, body, result, response, drmDeviceId)
                        callback(null, result)
                        /*
                        let keyText = JSON.parse(responseBody).ckc.trim()
                        keyText = keyText.replace(/^"(.+(?="$))"$/, '$1')
                        callback(null, base64DecodeUint8Array(keyText))
                        */
                        //callback(null, base64DecodeUint8Array(responseBody))
                     })
                  }
               }
            }
         }
         else if (this.isWidevine()) {
            keySystems = {
               'com.widevine.alpha': {
                  //url: this._source.getStreamUrlLicense,
                  //licenseHeaders: getQlHeaders(),
                  audioContentType: 'audio/mp4; codecs="mp4a.40.2"', // ec-3 and ac-3 (dolby) not supported
                  videoContentType: 'video/mp4; codecs="avc1.4D401E, avc1.4D4020, avc1.640020, avc1.64002A"',
                  audioRobustness: 'SW_SECURE_CRYPTO',
                  videoRobustness: 'SW_SECURE_CRYPTO',

                  getLicense: function (emeOptions, keyMessage, callback) {
                     //console.debug("widevine: fetch key: laUrl %s, keyMsg %o, emeOptions %o", licenseUrl, keyMessage, emeOptions)
                     videojs.xhr({
                        url: licenseUrl,
                        method: 'POST',
                        responseType: 'arraybuffer',
                        body: keyMessage,
                        headers: getQlHeaders()
                     }, function (err, response, responseBody) {
                        // test purpose
                        //response.statusCode = 403
                        // MTVW-927: check for 403 and 500
                        const error = checkLicenseError(response.statusCode)
                        console.debug("getLicense response status %s, err %o, rsp %o, body %o", response.statusCode, err, response, responseBody)
                        if (error) {
                           if (!rootStore.page.Player.error) rootStore.page.Player.error = error
                           return
                        }
                        if (err) {
                           console.error("getLicense error", err, response, responseBody)
                           // assuming EME will generate an error
                           callback(err)
                           return
                        }
                        console.debug("getLicense err %o, rsp %o, rspBody %o, cb %o, status %s, emeOptions %o", err, response, responseBody, callback, response.statusCode, emeOptions)
                        callback(null, responseBody)
                     })
                  }
               }
            }
         }
      }
      return keySystems
   }

   _initPlayer() {
      if (!this._source) return
      this.options = {
         // causes problems on iOS (multiple NotAllowedErrors)
         autoplay: false,   // true: the same as having attribute on the video element, will use browsers autoplay
         //poster: "img/cover-no-photo.gif",
         preload: "auto",
         //preload: "none",
         //enableSourceset: false,
         //playbackRates: [0.5, 1, 1.25, 1.5, 2],
         //width: "auto",
         //height: "auto",
         //fill: true,
         fluid: true,
			/*
			fluid: false,
			responsive: false,
			fill: true,
			aspectRatio: undefined,
			*/
         //position: "absolute",
         controls: false,
         liveui: false,
         //controlBar: false,
         loadingSpinner: false,
         //errorDisplay: false,
         //responsive: true,
         /*
         inactivityTimeout: 0,
         normalizeAutoplay: true,
         nativeControlsForTouch: false,
         */
         userActions: {
            click: false,
            doubleClick: false,
            hotkeys: false,
         },
         /*
         children: {
            controlBar: {
               fullscreenToggle: false,
            },
         },
         */
         // textTrackDisplay is needed for emulated text tracks!
         children: ["textTrackSettings", "textTrackDisplay"],
         restoreEl: true, // see also comment in PlayerVjs.css
         //restoreEl: this._refElemContainer,
         // MTVW-444, MTVW-788, MTVW-787: for iPhone
         playsinline: true,
         html5: {
            /*
            hls: {
               //overrideNative: true
               // activating 'withCredentials' crashes Chrome
               //withCredentials: true
            },
            */
            vhs: {
               // set to false for Safari eme
               // obsolete: has to be true for native subtitles on Safari! -> handled dynamically
               // must be false for Safari to use native HLS, otherwise green tint issue on Safari, see MTVW-560
               // MTVW-735, MTVW-736, MTVW-738: overrideNative on Android
               overrideNative: !videojs.browser.IS_SAFARI,
               allowSeeksWithinUnsafeLiveWindow: true,
               enableLowInitialPlaylist: true,
               useBandwidthFromLocalStorage: false,
               // MTVW-560: doesn't fix green screen issue
               // if set to higher value the following channels don't play anymore:
               // 5+, 6+, TV 24, S1, TV 25, Prosieben Maxx, Sixx, TLC, Anixe HD Serie, N24 Doku, Sport 1
               // MTVW-731: Dash tuning, Firefox has DASH problems with 4500000
               bandwidth: this.isDash() && !videojs.browser.IS_FIREFOX ? 4500000 : 1100000,
               //parse708captions: true,
               //useCueTags: false,
               //experimentalLLHLS: true,
               //useForcedSubtitles: true,
               //cacheEncryptionKeys: true,
               // MTVW-744
               // MTVW-896: set to false to check if it helps for stuck replay ads
               useDtsForTimestampOffset: false,
               // may cause distortions with dash/widevine
               //calculateTimestampOffsetForEachSegment: true,
               playlistExclusionDuration: 5,
               maxPlaylistRetries: 5,
               // MTVW-774: Android / Chrome / Edge replay stutter
               //useNetworkInformationApi: isAndroid() && (browserName().indexOf("chrome") >= 0 || browserName().indexOf("edge") >= 0),
               // MTVW-768: address Dash strem stuttering when restarting/pausing live stream
               // Firefox seems to have sometimes problems with useNetworkInformationApi
               useNetworkInformationApi: this.isDash() && !videojs.browser.IS_FIREFOX,
               //experimentalBufferBasedABR: isAndroid() && (browserName().indexOf("chrome") >= 0 || browserName().indexOf("edge") >= 0),
               //useDevicePixelRatio: true,
               //bufferBasedABR: true,
               //llhls: true,
               // MTVW-731: Dash tuning
               limitRenditionByPlayerDimensions: false,
               //limitRenditionByPlayerDimensions: videojs.browser.IS_SAFARI,
               //maxBufferLength: 30,
               //maxMaxBufferLength: 60
					handlePartialData: true, // MTVW-1062: does not change behavior of stalled seeking state
					/* MTVW-1049: following settings don't help, player might still seek and jump to end of replay ad
               maxGapSkip: 2, // [seconds]
               //experimentalBufferBasedABR: true,	// MTVW-1062: does not change behavior of stalled seeking state
					*/
            },
            //nativeAudioTracks: isIphone(), //false,
            // Needs to be true with overrideNative: false on Safari
            nativeAudioTracks: true,
            // no subtitles if set to false! Crashes FireFox on Windows if true
            // -> use emulated text tracks because of eme issue
            //nativeTextTracks: isIphone(), //false,
            // Needs to be true with overrideNative: false on Safari
            nativeTextTracks: !this.isDash() ? !videojs.browser.IS_FIREFOX : true,
            /*
            textTrackSettings: {
               backgroundColor: "#000",
               backgroundOpacity: "0",
               edgeStyle: "uniform",
            },
            */
            nativeVideoTracks: true,
            // MTVW-444, MTVW-788, MTVW-787
            nativeControlsForTouch: false,
         },
         enableDocumentPictureInPicture: false,
         disablePictureInPicture: false
      }

      videojs.options.autoSetup = false
      // MTVW-774: Android / Chrome / Edge replay stutter
      // Buffer sizes halved, issues with marker jumpings if even smaller
      if (/*this._source?.type !== SourceTypeList.ChannelLive &&*/
         isAndroid() && (browserName().indexOf("chrome") >= 0 || browserName().indexOf("edge") >= 0)) {
         videojs.Vhs.GOAL_BUFFER_LENGTH = 15
         videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = 30
         //videojs.Vhs.BACK_BUFFER_LENGTH = 15
      }

      // avoid problems function grabs items from player
      let iSec = 0
      const updateCheck = (bForce = null, ...params) => {
         // TODO joachim: _updateInternal calls are limited to one per second here, may swallow events
         let iSecCurr = Math.floor(Date.now() / 1000)
         if (bForce === true) {
            // always handle forced events, no accounting!
         } else if (iSec !== iSecCurr) {
            iSec = iSecCurr
         } else {
            return
         }
         this._updateInternal(...params)
      }

      const apiCreated = this._api != null
      const apiDisposed = !this._api || this._api?.isDisposed()

      //videojs.registerPlugin("ttml", Ttml)
      console.debug("plugins", videojs.getPlugins())
      const eme = videojs.getPlugins(["eme"])
      console.debug("eme", eme, typeof eme?.eme /*this._api?.eme*/, videojsContribEme)
      //videojs.deregisterPlugin("eme")
      if (this.isFairplay() || this.isWidevine()) {
         // set to false for Safari eme
         /* obsolete: overrideNative must be false to fix the green tint issue on Safari
         if (this.isFairplay()) this.options.html5.vhs.overrideNative = false
         else this.options.html5.vhs.overrideNative = true
         */
         if (!eme) {
            // register eme plugin for drm
            const result = videojs.registerPlugin("eme", videojsContribEme)
            console.debug("adding plugin eme", result, videojs.getPlugins())
         }
      }
      /* obsolete: overrideNative must be false to fix the green tint issue on Safari
      else {
         // No subtitles on Safari if set to false!
         this.options.html5.vhs.overrideNative = true // !isIphone()
      }
      */

      const elemVideo = document.getElementById('vjs_' + this.__path + '_html5_api')
      //let elemWrap = document.getElementById('wrap_' + this.__path)
      let elemWrap = document.getElementById('vjs_' + this.__path)
      /*
      if (elemWrap && !elemVideo) {
         console.debug("removing elemWrap %o, vElem", elemWrap, elemVideo)
         elemWrap.remove()
         elemWrap = null
      }
      */
      const videos = document.getElementsByClassName("video-js")
      //console.debug("existing elemVideo %o, ref %o, wrap %o, videos %o", elemVideo, this._refElemContainer, elemWrap, videos)
      //if (apiCreated) {
      /*
      if (apiCreated) console.debug(">>> api reset", elemVideo)
      if (!isIphone()) this._api?.pause() // autoplay issue on iPhone
      this._api?.reset()
      */
      // remove listeners
      //this._api?.off()
      //if (!this._api?.isDisposed()) this._api?.dispose()
      //}
      //if (apiCreated && !this._api?.isDisposed()) this._api?.dispose()
      // don't dispose again if already done
      /*
      //if (!this._refElemContainer && apiCreated && !this._api?.isDisposed()) {
         console.debug("Disposing")
         this._api?.dispose()
      }
      */
      //console.debug("vjsPlayer %o, %o, %o, %o", this._refElemContainer, document.getElementById('vjs_' + this.__path), elemVideo, this._refElemContainer)
      //console.debug("vjsPlayer %s, wElem %o, vElem %o, ref %o", this.__path, elemWrap, elemVideo, this._refElemContainer)
      console.debug("_initPlayer %s: disposed %s, ref %o, tvPlayer %o", this.__path, this._api?.isDisposed(), this._refElemContainer, document.getElementById("tvPlayer"))
      console.debug("elemVideo", elemVideo)
      //if (this._refElemContainer && !elemWrap) {
      if (apiDisposed || (this._refElemContainer && !elemVideo)) {
         //if (this._refElemContainer && !document.getElementById('vjs_' + this.__path)) {
         //console.debug("_initPlayer %o, %o", this, this._refElemContainer)
         // class vop-video would not appear in the video element but in wrap
         //const html = `<div id='myWrap' className='myWrap' <div id=${'wrap_' + this.__path} data-vjs-player> <video id=${'vjs_' + this.__path} className='video-js' /> </div>`
         /**/
         console.debug(">>> create html", elemWrap, elemVideo)
         // causes autoplay issues on iPhone
         //const html = `<div id=${'wrap_' + this.__path} className='myWrap'> <video id=${'vjs_' + this.__path} className='video-js' /> </div>`
         //const html = `<div id=${'wrap_' + this.__path} className='myWrap'> <div data-vjs-player> <video id=${'vjs_' + this.__path} className='video-js' /> </div> </div>`
         // Do not use both data-setup and an options object
         //const html = `<div data-vjs-player> <video id=${'vjs_' + this.__path} className='video-js' data-setup='{}'/> </div>`
         const html = `<div data-vjs-player> <video id=${'vjs_' + this.__path} className='video-js'/> </div>`
         // NOTE: there is a custom controls issue when using innerHTML
         //this._refElemContainer.innerHTML += html
         //this._refElemContainer.insertAdjacentHTML("afterbegin", html)
         this.videoNode = document.getElementById('vjs_' + this.__path)

         /*
         if (elemWrap && !elemVideo) {
            console.debug("removing elemWrap %o, vElem", elemWrap, elemVideo)
            //elemWrap.insertAdjacentHTML("afterbegin", "<div data-vjs-player> <video id=${'vjs_' + this.__path} className='video-js' />")
            //elemWrap.remove()
            //elemWrap = null
         }
         else {
            const html = `<div id=${'wrap_' + this.__path} className='myWrap'> <video id=${'vjs_' + this.__path} className='video-js' /> </div>`
            // NOTE: there is a custom controls issue when using innerHTML
            //this._refElemContainer.innerHTML += html
            this._refElemContainer.insertAdjacentHTML("afterbegin", html)
            this.videoNode = document.getElementById('vjs_' + this.__path)
         }
         */
         console.debug("videoNode", this.videoNode, 'vjs_' + this.idDomPlayer)
         console.debug("contains", this._refElemContainer?.contains(elemWrap), this._refElemContainer?.contains(elemVideo), this._refElemContainer)
         //this.videoNode.style.display = "flex"
         /**/
      }
      else {
         console.debug("_initPlayer %s: _refElemContainer %o, apiCreated %o", this.__path, this._refElemContainer, apiCreated)
         return
      }

      //console.debug("vjsPlayer %o, %o, %o, %o", this._refElemContainer, document.getElementById('vjs_' + this.__path), elemVideo, this._refElemContainer)
      //if (!apiCreated) {
      if (apiDisposed) {
         /*
         videojs.hook('beforeerror', function (player, err) {
            console.error("beforeerror", player, err)
            const error = player.error()

            // prevent current error from being cleared out
            if (err === null) {
               return error
            }
            return err
         })
         */
         //if (!elemVideo) {
         //this.options.restoreEl = document.getElementById("tvPlayer")
         // MTVW-788, MTVW-787: this.idDomPlayer instead of this.__path
         this._api = videojs('vjs_' + this.idDomPlayer, this.options)
         this._api?.crossorigin("anonymous")
         //this._api?.reset()
         console.debug(">>> create api: disposed", this.__path, this._api?.isDisposed(), this._refElemContainer, document.getElementById("tvPlayer"))
      }

      /*
      const tvPlayer = document.getElementById("tvPlayer")
      console.debug("dimensions", tvPlayer, tvPlayer.clientWidth, tvPlayer.clientHeight)
      this._api?.dimensions(tvPlayer.clientWidth, tvPlayer.clientHeight)
      */
      //this._api?.dimension('width', '100%')
      //this._api?.dimension('height', '100%')

      //if (apiCreated) {
      if (!apiDisposed) {
         //if (elemVideo) {
         console.debug("_initPlayer %s: _api already created", this.__path)
         return
      }

      // Triggered when a Component is ready. -> see this._api?.ready
      /*
      this._api?.on('ready', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: ready", this.__path, this._api?.readyState?.(), event)
            // autoplay not allowed if commented
            //return
            console.debug("paused on ready", this._api?.paused())
            //this._waitPlayer.setResolve()
            //this.tech.emulateTextTracks()
            this.tech?.on?.('usage', (e) => {
               //console.debug("VJS USAGE: ", this.__path, e.name, e)
               // done in 'loadedmetadata'
               //if (e.name === "vhs-audio-change") this.audioTracksContainer.setTracksFromStream()
               //if (e.name.includes("webvtt")) this.subtitleTracksContainer.setTracksFromStream()
               //if (e.name.includes("vhs-rendition-change-bandwidthupdate")) console.debug("BANDWIDTH", this._api?.tech()?.vhs.stats.bandwidth)
               console.debug("BANDWIDTH", this.tech?.vhs?.stats?.bandwidth)
            })
            this._waitPlayer.setResolve()

            // does not work
            //const settings = this.textTrackSettings
            //console.debug("textTrackSettings", settings, this)
            //settings.setValues({
            //   "backgroundColor": "#000",
            //   "backgroundOpacity": "0",
            //   "edgeStyle": "uniform",
            //})
            //settings.updateDisplay()

            //videojs.log.level('all')
         }, "VJS EVENT: onready")
      })
      */

      // Fires when the loading of an audio/video is aborted.
      this._api?.on('abort', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: abort", this.__path, this._api?.currentTime(), event)
         }, "VJS EVENT: abort")
      })

      // The media has a readyState of HAVE_FUTURE_DATA or greater.
      this._api?.on('canplay', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: canplay", this.__path, this._api?.currentTime(), event)
            this._waitStream.setResolve()
            /*
            if ((!this._isPausedUser && this.isLiveSource) || this._api?.duration() === Infinity) {
               // VJS Safari: Safari may generate pauses
               // (https://blog.videojs.com/autoplay-best-practices-with-video-js/)
               if (this._api?.paused()) {
                  this.isStreamLoaded = true
                  this.setPlay("canplay")
               }
            }
            */
         }, "VJS EVENT: canplay")
      })

      // The media has a readyState of HAVE_ENOUGH_DATA or greater. This means that the entire media file can be played without buffering.
      this._api?.on('canplaythrough', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: canplaythrough", this.isPaused, this.isPausedUser, this.__path, this._api?.currentTime(), event)
            // for iPhone
            if (isIphone()) this.setIsPosterVisible(false, "canplaythrough", false, 300)
            // CHANGE FOR AUTOPLAY false
            // Safari sometimes generates an 'ended' event and the initial setPlay from preloadEvent doesn't start!
            // MTVW-444
            if (this.__parent.playerFg) {
               this.setPause()
            }
            // NECESSARY? Needed on iPhone for replay ads, otherwise ad can get stuck
            if (/**/!this.preloadReq &&/**/ !this.isPaused && this._api?.paused()) this.setPlay("canplaythrough")
         }, "VJS EVENT: canplaythrough")
      })

      this._api?.on('controlsdisabled', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: controlsdisabled", this.__path, this._api?.currentTime(), event)
         }, "VJS EVENT: controlsdisabled")
      })

      this._api?.on('controlsenabled', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: controlsenabled", this.__path, this._api?.currentTime(), event)
         }, "VJS EVENT: controlsenabled")
      })

      // Called when the player is being disposed of.
      this._api?.on('dispose', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: dispose", this.__path, event)
            this._waitDisposed.setResolve()
         }, "VJS EVENT: dispose")
      })

      this._api?.on('durationchange', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: durationchange", this.__path, this._source.type, this._api?.currentTime(), this._api?.duration(), event)
            //return
            if (this._api?.duration() === Infinity) {
               //this._api?.currentTime(1e101)
               /*
               this._api?.on('timeupdate', (event) => {
                  console.debug("ontimeupdate")
                  this.ontimeupdate = () => {
                     return
                  }
                  this._api?.currentTime = 0
                  return
               })
               */
            }
            //if (this.isLiveSource && this._api?.duration() > 0) this._waitSeekCompleted.setResolve()

            // NOTE VJS: replay streams that include part of live stream are stuck here with no furter events
            // Workaround: generate _waitSeekCompleted.setResolve()
            /*
            if (this._source.type === "ChannelReplay" && this._api?.duration() === Infinity) {
               //if (this._api?.duration() > 0) {
               // TODO VJS: recheck, why no seeked event?
               // this.tech.vhs is not yet valid!
               this._waitSeekCompleted.setResolve()
               console.debug("_waitSeekCompleted", this._waitSeekCompleted)
            }
            */
            // iPhone
            //this._waitSeekCompleted.setResolve()
         }, "VJS EVENT: durationchange")
      })

      // Fires when the current playlist is empty.
      this._api?.on('emptied', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: emptied", this.__path, this._api?.currentTime(), event)
         }, "VJS EVENT: emptied")
      })

      // Fired when the end of the media resource is reached (currentTime === duration)
      this._api?.on('ended', (event) => {
         this.safeWrapper(() => {
            if (!this.apiGuard) {
               console.debug(timeNow() + " VJS EVENT: ended API DISPOSED")
               return
            }
            console.debug(timeNow() + " VJS EVENT: ended", this.__path, this._api?.currentTime(), this._api.duration(),this.playCompleted, this.setPlayResult, apiDisposed, this._source, event)
            // MTVW-531, MTVW-529: Direct execution within player callback may cause impredicable behavior on Safari
            //this._source?.handleStreamOnEnd?.()
            // MTVW-481
            // (VJS: Do only for playerAds, otherwise isPlayerActive will return false!)
            // MTVW-678: -> /*this.__path === "playerAds" &&*/
            // Safari might generate event before the play has started
            if (/*this.__path === "playerAds" &&*/ !this.playCompleted && this.setPlayResult) {
               this.playCompleted = true
               this._source?.playCompleteEvent?.(this.positionTimeStamp, this.durationMetaData * 1000, this._api?.currentTime() * 1000)
            }
            else {
               console.debug("IGNORING VJS EVENT: ended", this.setPlayResult, this._api?.paused())
               if (this.setPlayResult && this._api?.paused() && this.__path !== "playerAds") {
                  this._api?.play()
                  this.setIsPosterVisible(false, "ended")
               }
            }
            // MTVW-359: VO player generates pause event before the complete event
            //monitorState.setForcedPause(false)
            updateCheck(true, "ended")
         }, "VJS EVENT: ended")
      })

      // MTVW-727
      // This event fires when the player enters picture in picture mode
      this._api?.on('enterpictureinpicture', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: enterpictureinpicture", this.__path, event, event.pictureInPictureWindow)
            runInAction(() => { this.isInPip = true })
            updateCheck(true, "enterpictureinpicture")
            if (isSafari()) {
               const videoElem = this.__parent.getVideoElem()
               videoElem.style.width = event.pictureInPictureWindow.width + "px"
               videoElem.style.height = event.pictureInPictureWindow.height + "px"
               event.pictureInPictureWindow.addEventListener("resize", this.onPipWindowResize.bind(this))
            }
         }, "VJS EVENT: enterpictureinpicture")
      })

      this._api?.on('error', (event) => {
         this.safeWrapper(() => {
            const error = this._api?.error()
				console.debug(timeNow() + " VJS EVENT: error", this.__path, this._api?.currentTime(), error, event)
            if (this.errorCallback) {
               this.errorCallback(error)
               return
            }
            this.showPiP(false)
            const err = ErrorBase.CreateError(["Player code " + error?.code ?? "", error?.message, this], null, { code: error?.code, type: error?.status })
            if (this.openStreamDone) {
               // MTVW-682: RECHECK if only for above condition
               /*
               // MTVW-531, MTVW-529: Direct execution within player callback may cause impredicable behavior on Safari
               setTimeout(() => {
                  // MTVW-633: dispatch error
                  EventEmitter.dispatch("playerError", error)
                  // GT12
                  if (this._source) this._source?.errorEvent(err)
               })
               */
            }
            else {
               if (!this._waitStream?.isDone) this._waitStream.setReject(err)
               if (!this._waitSeekCompleted?.isDone) this._waitSeekCompleted.setReject(err)
            }
            // MTVW-682: RECHECK if only for openStreamDone
            // MTVW-531, MTVW-529: Direct execution within player callback may cause impredicable behavior on Safari
            setTimeout(() => {
               // MTVW-633: dispatch error
               console.error("vjs error openStreamDone=", this.openStreamDone, error, err)
               EventEmitter.dispatch("playerError", error)
               // GT12
               if (this._source) this._source?.errorEvent(err)
            })

         }, "VJS EVENT: error")
      })

      /* replaced with onFullScreenChange(ev)
      this._api?.on('fullscreenchange', (event) => {
         console.debug(timeNow() + " VJS EVENT: fullscreenchange", this.__path, this._api?.isFullscreen())
         //this.__parent.fullScreenChange(this._api?.isFullscreen())
         console.debug("dimensions", this._api?.currentDimensions(), this._api?.videoHeight(), this._api?.aspectRatio(), this.videoNode.videoHeight)
         const test = document.getElementById("vjs_playerDetailPageVo_html5_api")
         console.debug("", test, test.videoHeight, test.clientHeight)
         if (!this._api?.isFullscreen()) {
            this.setFullScreen(false)
         }
         updateCheck(false, "fullscreenchange")
      })
      */

		/* handled via event listener 'onFullScreenChange'
		this._api?.on('fullscreenchange', (event) => {
			console.debug("on fullscreenchange", event)
      })
		*/

      // MTVW-727
      // This event fires when the player leaves picture in picture mode
      this._api?.on('leavepictureinpicture', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: leavepictureinpicture", this.__path, this._api?.currentTime(), event)
            runInAction(() => {
               this.isInPip = false
               // MTVW-727b
               this.__parent.pipCloseEvent()
            })
            if (isSafari()) {
               const videoElem = this.__parent.getVideoElem()
               videoElem.style.width = "100%"
               videoElem.style.height = "100%"
               event.pictureInPictureWindow.removeEventListener("resize", this.onPipWindowResize.bind(this))
            }
            updateCheck(true, "leavepictureinpicture")
         }, "VJS EVENT: leavepictureinpicture")
      })

      // Fires when the browser has loaded the current frame of the audio/video.
      // Fired when the player has downloaded data at the current playback position
      this._api?.on('loadeddata', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: loadeddata", this.__path, this._api?.currentTime(), event)
            //if (!this.isLiveSource) this._waitStream.setResolve()
            ///if (this.streamStartPosition) this.setPositionTimeStamp(this.streamStartPosition)
            //this.isStreamLoaded = true
         }, "VJS EVENT: loadeddata")
      })

      // Dispatched when the player has initial duration and dimension information, in other words,
      // when the first segment is downloaded. For live videos the loadedmetadata event won't be dispatched
      // until the user clicks play.
      this._api?.on('loadedmetadata', (event) => {
         this.safeWrapper(() => {
            this.durationMetaData = this._api?.duration?.()
            console.debug(timeNow() + " VJS EVENT: loadedmetadata", this.__path, this._api?.currentTime(), this.durationMetaData, event)
            //const seekRange = this._api?.buffered()
            //console.debug("seekable", seekRange.start(0), seekRange.end(0))
            //console.debug("audioTracks=", this._api?.audioTracks())
            console.debug("bandwidth", this.tech?.vhs?.stats?.bandwidth)
            console.debug("featuresNativeTextTracks", this.tech?.featuresNativeTextTracks)
            console.debug("representations", this.tech?.vhs?.representations?.())
				    console.debug("tech=", this.tech?.vhs)
            // iPhone would be stuck here during replay
            // RECHECK: For vjs 7.20.3 only??? for replay switching
            if (isIphone()) this._waitSeekCompleted.setResolve()
            //videojs.options.html5.nativeTextTracks = true
            //this.audioTracksContainer.setTracksFromStream()
            //this.subtitleTracksContainer.setTracksFromStream()
            /*
            var settings = this._api?.textTrackSettings
            settings.setValues({
               "backgroundColor": "#000",
               "backgroundOpacity": "0",
               "edgeStyle": "uniform",
            })
            settings.updateDisplay()
            console.debug("textTrackSettings", settings, this._api, this.tech)
            */
            // ADJUSTED LIVE POSITION / REPOSITIONING
            //this.initialStreamPositionUnix = this.getPlaylistStart() + this._api?.currentTime() * 1000
            let playlistStart = this.getPlaylistStart()
            console.debug("playlistStart %s, extPDT %s, initial %s", playlistStart, this.extProgramDateTime, moment(this.initialStreamPositionUnix).utc().toString())
            if (this.isLiveSource) {
               // Safari does not support MSE (Media Source Extension)
               if (!playlistStart && this.extProgramDateTime) playlistStart = this.extProgramDateTime
               if (!playlistStart) playlistStart = moment().utc().valueOf() - 73000  // fallback to approx. value
               this.initialStreamPositionUnix = moment(playlistStart).utc().valueOf()
               //window.ql_streamPosUnix = this.initialStreamPositionUnix / 1000
               console.debug("initial unix=", moment(this.initialStreamPositionUnix).toString(), moment().utc().valueOf() - this.initialStreamPositionUnix)
               //this.initialStreamPositionApi = this._api?.currentTime()
            }
            //if (this.streamStartPosition) this.setPositionTimeStamp(this.streamStartPosition)
         }, "VJS EVENT: loadedmetadata")
      })

      // Fired when the user agent begins looking for media data
      // Since the loadstart event may happen before the player is ready,
      // it is best practice to set the event listener for this event before calling the player's ready() function.
      this._api?.on('loadstart', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: loadstart", this.__path, this._api?.currentTime(), event)
            updateCheck(true, "loadstart")
            // CHANGE FOR AUTOPLAY false
            this._waitStream.setResolve()
            //this.setPlay("loadstart loadstart")
            /*
            ////if (!this.isLiveSource) this._waitStream.setResolve()
            //if (this.streamStartPosition) this.setPositionTimeStamp(this.streamStartPosition)
            this._clearWatchdog()
            if (this.isNearLive) {
               this.isStreamLoaded = true
               this.setPlay("loadstart nearLive")
            }
            */
         }, "VJS EVENT: loadstart")
      })

      // Fired whenever the media has been paused
      this._api?.on('pause', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: pause", this.__path, event)
            // VJS Safari: condition for this.openStreamDone
            // MTVW-531, MTVW-529: Direct execution within player callback may cause impredicable behavior on Safari
            if (this.openStreamDone) {
					// MTVW-1165: reflect pause state
					if (this.isInPip) this.setIsPausedUser(true)
               ///this.setIsPaused(true)
               // MTVW-359: prevent VO player generated pause events, add condition this._isStreamLoaded && this.__path !== "playerAds"
               // MTVW-788, MTVW-787: PauseAd was not displayed when pausing live stream
               // With the introduction of a background player in MTVW-444 the page.source in rootStore.page.player
               // changes when starting the background player.

               const isFgPlayer = this.__parent.playerFg !== null && this.__parent.playerFg?.__path === this.__path
               console.debug("PAUSED,", this.__parent.playerFg !== null, this.__parent.playerFg?.__path, this.__path)
               let isPausedUser = this.isPausedUser
               if (!isFgPlayer && this.__parent.playerFg) isPausedUser = false
               console.debug("PAUSED, pausedUser %s, isFgPlayer %s, isLoaded %s, source %o, fullScreen %s, iPhone %s, browser %s", isPausedUser, isFgPlayer, this._isStreamLoaded, this._source, this.isOnFullScreen(), isIphone(), browserName())
               if (!isFgPlayer && !isPausedUser && this._isStreamLoaded && this.__path !== "playerAds") {
                  //monitorState.setForcedPause(true)
                  // GT12: on iPhone with native controls we don't get the player controls events
                  //if (this.isOnFullScreen() && isIphone()) this._source.pauseEvent()
               }
               else if (this._isStreamLoaded && this.__path !== "playerAds") {
                  // GT12
                  console.debug("pauseEvent", this.__path, this.__parent.playerFg?.__path)
                  this._source?.pauseEvent?.()
               }
            }
            updateCheck(true, "pause")
            this.setIsPaused(true)
            this._waitPaused.setResolve()
         }, "VJS EVENT: pause")
      })

      // Triggered whenever an Tech#play event happens. Indicates that playback has started or resumed.
      this._api?.on('play', (event) => {
         this.safeWrapper(() => {
            // https://github.com/videojs/video.js/issues/5987
            /*
            if (this._api?.currentTime() === 0) {
               this._api?.currentTime(0)
            }
            */
            console.debug(timeNow() + " VJS EVENT: play", this.__path, this._api?.currentTime(), event, "in PIP", this.isInPip)
				// MTVW-1165: reflect pause state
				if (this.isInPip) {
					this.setIsPausedUser(false)
					this._source?.removePauseAd()
				}
				updateCheck(true, "play")
         }, "VJS EVENT: play")
      })

      // fires when the playback rates in a player are changed
      this._api?.on('playbackrateschange', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: playbackrateschange", this.__path, this._api?.currentTime(), event)
         }, "VJS EVENT: playbackrateschange")
      })

      // The media is no longer blocked from playback, and has started playing.
      this._api?.on('playing', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: playing", this.__path, this._api?.duration(), this._api?.currentTime(), event)
            /*
            const wrapper = document.getElementsByClassName("vjs_playerDetailPageVo-dimensions")[0]
            console.debug("wrapper", document.getElementsByClassName("vjs_playerDetailPageVo-dimensions"), wrapper)
            if (wrapper) {
               wrapper.width = "100%"
               wrapper.height = "100%"
            }
            */
            //this.audioTracksContainer.setTracksFromStream()
            //this.subtitleTracksContainer.setTracksFromStream()
            const seekRange = this._api?.buffered()
            console.debug("seekable", seekRange.start(0), seekRange.end(0))
            this.setIsPaused(false)
            this.setIsLoading(false, "playing")
            // MTVW-779: Make sure that play resumes when the 2nd ads-mock ad finishes
            this.playCompleted = false
            this._clearWatchdog()
            updateCheck(true, "playing")
            // MTVW-514: Moved from VO_OSMP_CB_PLAY_STARTED (recordings bookmarks)
            // MTVW-531, MTVW-529: Direct execution within player callback may cause impredicable behavior on Safari
            if (this.isNearLive && this.streamStartPosition) {
               //console.debug("playing: setting again streamStartPosition")
               ///this.setPositionTimeStamp(this.streamStartPosition)
               this.streamStartPosition = null
               // MTVW-444, MTVW-788, MTVW-787: causes black screen on iPhone and iPad after replay ad when
               // restarting paused live stream
               /// /*if (!isIphone())*/ this.setIsPosterVisible(true, "playing")
            }
            else {
               //this.setIsPosterVisible(false, "playing")
               this._source?.playStartEvent?.()
               if (this._source?.isOnDiscover) rootStore.page.Discover.playStartEvent()
            }
            console.debug("isPiPActive", this.isInPip)
            // MTVW-727b
            // if starting a show in the tvGuide while PiP was active in liveTV before we could theoretically keep PiP
            if (this.isInPip) {
               // mitigate issue (not playing in PiP, black player) when switching back to live Tv
               console.debug("nbPlayingEvents %s, liveChannelChange %s", this.nbPlayingEvents, this.liveChannelChange)
               // avoid issues with multiple 'playing events'
               if (isSafari() && !this.liveChannelChange && this.nbPlayingEvents === 0) this.showPiP(false)
               if (this.nbPlayingEvents === 0) this.showPiP(true)
            }
            else {
               this.showPiP(false)
            }
            this.liveChannelChange = false
            this.nbPlayingEvents++

            console.debug("bandwidth", this.tech?.vhs?.stats?.bandwidth)

            // detect change of rendering quality
            /*
            let tracks = this._api?.textTracks()
            let segmentMetadataTrack = null
            for (let i = 0; i < tracks?.length; i++) {
               console.debug("text track: ", tracks[i].kind, tracks[i].language, tracks[i])
               if (tracks[i].label === 'segment-metadata') {
                  segmentMetadataTrack = tracks[i]
               }
            }
       
            tracks = this._api?.audioTracks()
            for (let i = 0; i < tracks?.length; i++) {
               console.debug("audio track: ", tracks[i].language, tracks[i])
            }
       
            let previousPlaylist = null
            console.debug("segmentMetadataTrack", segmentMetadataTrack)
            if (segmentMetadataTrack) {
               segmentMetadataTrack.on('cuechange', function () {
                  let activeCue = segmentMetadataTrack.activeCues[0]
                  console.debug("activeCue", activeCue)
                  if (activeCue) {
                     if (previousPlaylist !== activeCue.value.playlist) {
                        console.debug('Switched from rendition ' + previousPlaylist +
                           ' to rendition ' + activeCue.value.playlist)
                        console.debug("cue", activeCue.value)
                     }
                     previousPlaylist = activeCue.value.playlist
                  }
               })
            }
            */
         }, "VJS EVENT: playing")
      })

      // Fired while the user agent is downloading media data.
      this._api?.on('progress', (event) => {
         this.safeWrapper(() => {
            // TODO: generate timestamp -> timeupdate below
            //console.debug(timeNow() + " VJS EVENT: progress", this.__path, this._api?.currentTime())
         }, "VJS EVENT: progress")
      })

      // Fires when the playing speed of the audio/video is changed
      this._api?.on('ratechange', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: ratechange", this.__path, this._api?.playbackRate(), this._api?.getVideoPlaybackQuality(), event)
         }, "VJS EVENT: ratechange")
      })

      // Fired when the player has finished jumping to a new time
      this._api?.on('seeked', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: seeked", this._api?.currentTime(), this._api?.duration(), this.__path, event)
            const apiTime = this._api?.currentTime()
            //console.debug("position after seek %s %o %o (%o)", this.__path, this._apiGetPosition(), moment(this._brokenApiPlayTimeTs()).utc().format(TIME_FORMAT_BACKEND), moment(this._brokenApiPlayTimeTs() || 0).format(TIME_FORMAT_BACKEND), moment().format(TIME_FORMAT_BACKEND))
            console.debug("seek target %s", this.__path, this.targetApiTime * 1000 + this.initialStreamPositionUnix, moment(this.targetApiTime * 1000 + this.initialStreamPositionUnix).utc().format(TIME_FORMAT_BACKEND))
            console.debug("position after seek %s", this.__path, this.positionTimeStamp, moment(this.positionTimeStamp).utc().format(TIME_FORMAT_BACKEND), apiTime)
            const seekRange = this._api?.buffered()
            console.debug("seekable", seekRange.start(0), seekRange.end(0))
            this._waitSeekCompleted.setResolve()
            this.setIsLoading(false, "seeked")
            // avoid black screen on some Safari browsers, the black screen never happened on my machine
            // use a delay because more than 1 'seeked' event might happen
            if (isSafari()) this.setIsPosterVisible(false, "seeked", false, 300)
            this._clearWatchdog()
            updateCheck(true, "seeked")
            if (this.targetApiTime) {
               // NOTE VJS: workaround for near live replay streams that end in the live stream
               // MTVW-740: add condition this.isNearLive
               if (this.isNearLive && Math.abs(this.targetApiTime - apiTime) > 1) {
                  console.debug("setting targetApiTime", this.targetApiTime, apiTime)
                  this._api?.currentTime(this.targetApiTime)
                  this.targetApiTime = null
                  //this.setIsPosterVisible(true)
               }
               else this.targetApiTime = null
            }
            else {
               //this.setIsPosterVisible(false, "seeked")
            }
         }, "VJS EVENT: seeked")
      })

      // Fired whenever the player is jumping to a new time
      this._api?.on('seeking', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: seeking", this._api?.currentTime(), this.__path, event)
            this.setIsLoading(true, "seeking")
            this._startWatchdog()
            updateCheck(true, "seeking")
         }, "VJS EVENT: seeking")
      })

      this._api?.on('sourceset', (event) => {
         console.debug("sourceset", event)
      })

      this._api?.on('stalled', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: stalled", this.__path, this._api?.currentTime(), event)
            // MTVW-1112: watchdog for stalled program would be triggered on Safari
            /*if (this.__parent.isAdsPlayer) */ this._clearWatchdog()
            console.debug("dur %s, time %s", this.durationMetaData, this._api?.currentTime())
         }, "VJS EVENT: stalled")
      })

      this._api?.on('suspend', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: suspend", this.__path, event)
         }, "VJS EVENT: suspend")
      })

      // Fires when the browser is trying to get media data, but data is not available.
      this._api?.on('tap', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: tap", this.__path, event)
         }, "VJS EVENT: tap")
      })

      // Fired when the current playback position has changed * During playback this is fired every 15-250 milliseconds, depending on the playback technology in use.
      this._api?.on('timeupdate', (event) => {
         this.safeWrapper(() => {
            // TODO: generate timestamp
            //console.debug(timeNow() + " VJS EVENT: timeupdate", this.__path, this._api?.currentTime())
            updateCheck(true, "timeupdate", this._api?.currentTime())
            // VJS workaround: Safari does not generate always 'ended' event
            if (this.__path === "playerAds" && !this.playCompleted && this.durationMetaData && this._api?.currentTime() >= this.durationMetaData) {
               console.warn("AD ENDED", this._api?.currentTime(), this.durationMetaData)
               // MTVW-
               this.playCompleted = true
               //console.debug("timeupdate playCompleted", this.durationMetaData * 1000, this._api?.currentTime() * 1000)
               this._source?.playCompleteEvent?.(this.positionTimeStamp, this.durationMetaData * 1000, this._api?.currentTime() * 1000)
               // generate once
               this.durationMetaData = null
            }
         }, "VJS EVENT: timeupdate")
      })

      this._api?.on('usingcustomcontrols', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: usingcustomcontrols", this.__path, this._api?.currentTime(), event)
            //this.setVolume(this._api?.volume() * 100, true)
         }, "VJS EVENT: usingcustomcontrols")
      })

      this._api?.on('usingnativecontrols', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: usingnativecontrols", this.__path, this._api?.currentTime(), event)
            //this.setVolume(this._api?.volume() * 100, true)
         }, "VJS EVENT: usingnativecontrols")
      })

      // Fires when the volume has been changed
      this._api?.on('volumechange', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: volumechange", this.__path, this._api?.currentTime(), event, this._api?.volume())
            //this.setVolume(this._api?.volume() * 100, true)
         }, "VJS EVENT: volumechange")
      })

      // A readyState change on the DOM element has caused playback to stop.
      this._api?.on('waiting', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: waiting", this.__path, this._api?.currentTime(), this._api?.duration(), event)

            /* MTVW-1112 trial
            // Check if we are at a known discontinuity point
            const currentTime = this._api?.currentTime()

            // Logic: If stuck for more than 1 second in 'waiting'
            setTimeout(() => {
               if (this._api?.readyState() < 3 && !this._api?.paused()) {
                  console.warn("FairPlay stall detected. Force-syncing...")

                  // 1. Small jump past the gap
                  this._api?.currentTime(this._api?.currentTime() + 0.2)

                  // 2. Toggle playback to kick the CDM (Content Decryption Module)
                  this._api?.pause()
                  setTimeout(() => this._api?.play(), 50)
               }
            }, 1000)
            */

            this.setIsLoading(true, "waiting")
            // MTVW-1112: Safari condition
            if (!isSafari()) this._startWatchdog()
            updateCheck(true, "waiting")
         }, "VJS EVENT: waiting")
      })

      this._api?.on('waitingforkey', (event) => {
         this.safeWrapper(() => {
            console.debug(timeNow() + " VJS EVENT: waitingforkey", this.__path, event)
         }, "VJS EVENT: waitingforkey")
      })

      this._intervalId = setInterval(() => {
         if (this.isPlayerActive) {
            runInAction(() => { updateCheck(true, "interval_check") })
         }
         // MTVW-588: increase sampling rate for improved accuray
         // TODO VJS: 100 probably not needed
         //this.canResetPlayer() // Test output
      }, this.isAdSource ? 100 : 1000)

      console.debug("node", this.videoNode, this)
      if (this.videoNode) {
         this.videoNode.setAttribute("webkit-playsinline", true)
         this.videoNode.setAttribute("playsinline", true)
      }
      //this._api?.init(this._getPlayerConfig)
   }

   setIsLoading(isLoading = false, info) {
      if (this.isLoading !== isLoading) {
         console.debug("setIsLoading", info, isLoading)
         this.isLoading = isLoading
      }
      return this
   }

   // MTVW-727b
   onPipWindowResize(event) {
      const { width, height } = event.target
      //console.debug("onPipWindowResize", width, height, event)
      const videoElem = this.__parent.getVideoElem()
      if (videoElem) {
         videoElem.style.width = width + "px"
         videoElem.style.height = height + "px"
      }
   }

   // MTVW-727b: canResetPlayer(), in PiP mode we cannot always reset or stop the active player
   canResetPlayer() {
      const canResetInOverlay = !(this.__parent.pipPlayer !== null && this.__parent.pipPlayer?.__path === this.__path)
      const canReset = !this.isInPip && !this.__parent.pipPlayer || canResetInOverlay || this.__parent.isInLiveTv
      console.debug("canResetPlayer %s, inPiP %s, overlay %s, liveTV %s, player %s, pipPlayer %s, ctx %s, refElem %o",
         canReset, this.isInPip, canResetInOverlay, this.__parent.isInLiveTv, this.__path, this.__parent.pipPlayer?.__path, rootStore.page.OverlayEventDetails.callingContext, this._refElemContainer/*, ShadowRoot.pictureInPictureElement*/)
      return canReset
   }

   // different states for near live: wait for loadstart then postion, then wait for seeked
   // COMMON: _waitPlayer ('ready') - _waitStream ('loadstart')
   // LIVE: play
   // REPLAY / RECORDING: _waitSeekCompleted ('durationchange' ??), ('seeked')
   // NEAR LIVE: 
   openStreamAsync = flow(function* (preload = false, startPosition = null) {
      try {
         console.debug("@@openStreamAsync() %s -> this._source=%o, %s, preload %s, startPosition %s", this.__path, this._source, this.__path, preload, startPosition)
			//console.debug("@@openStreamAsync() %o -> this._refElemContainer=", this._refElemContainer)
			if (!this._source || (this._source?.Event?.channel?.isAdult && appFeatures.isAdultContentProtected)) {
            rootStore.setIsLoading(false)
            console.debug("@@openStreamAsync() return", this._source?.Event?.channel?.isAdult, appFeatures.isAdultContentProtected)
            return
         }

         if (startPosition) startPosition = moment(startPosition).utc().valueOf()
         // reset calculated items
         // done in _intiPlayer
         //this._api?.init(this._getPlayerConfig)
         /*
         if (this._api) {
            this._api?.dispose()
            this._waitPlayer.setReset()
            this._initPlayer()
            this._waitPlayer.setResolve()
         }
         */
         this.preloadReq = preload
         //if (isIphone()) yield sleep(1000)
         //this._initPlayer()
         // TODO: something seems to be asynchronous
         //if (isIphone()) yield sleep(1000)
         //this.setStop()
         this.error = null
         this.setIsLoading(true, "openStreamAsync start")
         this._waitPlayer.setReset()
         this._waitStream.setReset()
         this._waitSeekCompleted.setReset()
         this._waitDisposed.setReset()
         this._waitPaused.setReset()
         this.setIsPosterVisible(true, "openStreamAsync start")
         this._clearWatchdog()
         /*
         this.setIsPosterVisible(true)
         this.hidePosterAtSeekComplete = false
         this.hidePosterAtPlayStarted = true
         */
         this.isStreamLoaded = false
         this._isStreamLoaded = false
         this.isStreamGrowing = null
         /*
         this.bitrateAudioContainer = {}
         this.bitrateVideoContainer = {}
         this._internalStorage = {}
         */
         this.streamLiveDelay = 0
         this.streamCurrentDelay = 0
         this.positionTimeStamp = null
         this.positionTimeStamp60s = null
         this.playCompleted = false
         this.setPlayResult = false
         this.targetApiTime = null
         this.durationMetaData = null
         // MTVW-506: Fix replay from live
         this.initialStreamPositionUnix = null
         this.currentStreamPositionApi = 0
         this.initialStreamPositionApi = 0
         this._isPaused = false
         this._isPausedUser = false
         this.extProgramDateTime = null

         // reset prev audio/subtitle paths
         this.audioTracksContainer.setTracks(null)
         this.subtitleTracksContainer.setTracks(null)

         this.isNearLive = false

         this.openStreamDone = false
         this.nbPlayingEvents = 0
         this.isDisposed = false
         // don't reset
         //this.setMutedByBrowser(false)

         if (!this._refElemContainer || !this.idDomPlayer || !this.idDomElement) {
            console.warn("path %o, refElem %o, idDomPlayer %o, idDomElement %o", this.__path, this._refElemContainer, this.idDomPlayer, this.idDomElement)
            // MTVW-996: It can happen when switching quickly at startup from the Discover to the LiveTv page
            return
         }

			const adUrl = getUrlQueryParam("adUrl")?.replace("http://", "https://")
			if (adUrl) {
				const adSource = SourceAdPlaylist.create(this._source.__parent, adUrl + "?cb=" + Date.now())
				this._source = adSource
				this.__path = "playerAds"
			}

         // MTVW-549
         setQlWorkerHeaders(getQlHeaders())

         this.streamStartPosition = null
         if (this._source.playlist.seekable && this._source.playlist.eventStart > 0) {
            this.streamStartPosition = preload && startPosition ? startPosition : this._source.playlist.eventStart
            this.isNearLive = moment().utc().valueOf() <= this._source.playlist.end
            console.debug("streamStartPosition= %s, nearLive %s", moment(this.streamStartPosition).utc().format(TIME_FORMAT_BACKEND), this.isNearLive)
         }

         this._initPlayer()
         // https://videojs.com/guides/setup/
         // An important distinction between the above methods is that adding an listener for ready with on() must be done before the player is ready.
         // With player.ready(), the function is called immediately if the player is already ready.

         /**/
         this._api?.ready(() => {
            // https://github.com/videojs/video.js/issues/5987
            //this._api.vhs = null
            console.debug(timeNow() + " VJS EVENT: ready method", this.__path)
            console.debug("paused onready", this._api?.paused())
            //return
            //this.tech.emulateTextTracks()
            this.tech?.on?.('usage', (e) => {
               //console.debug("VJS USAGE: ", this.__path, e.name, e)
               // done in 'loadedmetadata'
               //if (e.name === "vhs-audio-change") this.audioTracksContainer.setTracksFromStream()
               //if (e.name.includes("webvtt")) this.subtitleTracksContainer.setTracksFromStream()
               //if (e.name.includes("vhs-rendition-change-bandwidthupdate")) console.debug("BANDWIDTH", this._api?.tech()?.vhs.stats.bandwidth)
               console.debug("BANDWIDTH", this.tech?.vhs?.stats?.bandwidth)
            })
            // NOTE: No log output on Safari for overrideNative: false
            if (appFeatures.persistedPlayerLog) videojs.log.level('all')
            else videojs.log.level('off')
            //this._api?.userActive(true)
            //this._api?.controls(false)  // for controls hiding
            //this._api?.usingNativeControls(false)
            //this._api?.debug(true)

            // MTVW-731: The videojs-contrib-eme plugin must be initialized before a source is loaded into the player
            // tech will only be available after having set the src
            if (this.isFairplay() || this.isWidevine()) {
               const keySystems = this._configureDrm()
               console.debug("keySystems %s, %s, %o, %o, %o", this.__path, typeof this._api?.eme, this._api, this._api?.eme, keySystems)
               // channel swiching in liveTv tab: if the keySystems have already been added this._api?.eme is not a function type anymore
               if (typeof this._api?.eme === "function") {
                  // EME: this initialization is needed on Safari
                  // the default value of firstWebkitneedkeyTimeout (1000) stalls encrypted HLS replay streams and recordings
                  // MTVW-743: firstWebkitneedkeyTimeout is a critial value on Safari, reduced from 100 to 10 ms
                  this._api?.eme?.({
                     keySystems: keySystems, firstWebkitneedkeyTimeout: 10,
                     //limitRenewalsMaxPauseDuration: 1,
                     //limitRenewalsBeforePlay: true
                  })
                  this._api?.eme?.initLegacyFairplay()
                  // EME: videojs stalls at init on Safari
                  //this._api?.eme?.()
                  /*
                  const emeCallback = function (error) {
                     if (error) {
                        console.error("emeCallback", error)
                     }
                     // do something else
                  }
                  const emeOptions = {
                     keySystems: keySystems
                  }
                  const suppressErrorsIfPossible = true
                  this._api?.eme?.initializeMediaKeys(emeOptions, emeCallback, suppressErrorsIfPossible)
                  */

                  /*
                  this._api?.eme.detectSupportedCDMs().then((res) => {
                     console.debug("CDMs", res)
                  })
                  */
               }
               console.debug("_api.eme", this._api?.eme)
               //this._api?.eme.options = { keySystems: keySystems }
               //videojs.options.autoSetup = false //?
            }
            this._waitPlayer.setResolve()
         })
         /**/

         // iPhone test
         /*
         if (isIphone()) {
            this.options.html5.vhs.overrideNative = false
            this.options.html5.nativeAudioTracks = false
            this.options.html5.nativeTextTracks = false
         }
         */

         videojs.options.autoSetup = false

         // MTVW-514: In case show is closed during replay ad
         if (startPosition) this.positionTimeStamp = startPosition

         // TODO fix up with correct live delay
         // TODO VJS set to 20 instead of 40?
         const liveOffset = moment.duration(40, "seconds")

         this.streamLiveDelay = liveOffset
         // TODO VJS: recheck, set to 0 for recordings
         this.initialStreamPositionApi = 0 //this._apiGetPosition()

         if (this.isLiveSource === true) {
            // playTimeTs works correctly for live streams (only!)
            this.initialStreamPositionUnix = this._brokenApiPlayTimeTs()
            this.streamLiveDelay = liveOffset
            //window.ql_streamPosUnix = this.initialStreamPositionUnix / 1000 - 36
         } else if (this.isAdSource === true) {
            this.initialStreamPositionUnix = this._brokenApiPlayTimeTs()
            this.streamLiveDelay = moment.duration(0, "seconds")
            console.debug("isAdSource, %s, %o, %o", this.initialStreamPositionUnix, this.streamLiveDelay, this._source)
         } // MTV-2967: Special handling with VO player V3.33 and V3.34 for
         // Safari on Mac and iOS and browsers on iPad
         /**/ // TODO VJS: recheck
         else if ((this._apiIsLive() === true) && this._isBrokenPositioning) {
            // BKM replay stream that is still growing starts at "live position"
            // MTVW-543
            this.initialStreamPositionUnix = (moment().subtract(liveOffset).utc().valueOf())
            //console.debug("initialStreamPositionUnix live %s", this.initialStreamPositionUnix)
         }
         /**/
         else {
            // BKM recording or replay stream, initial position is on start of stream
            // MTVW-543
            this.initialStreamPositionUnix = this._source.playlist.begin
            console.debug("initialStreamPositionUnix", moment(this.initialStreamPositionUnix).utc().format(TIME_FORMAT_BACKEND))
            this.streamLiveDelay = moment.duration(0, "seconds")
            if (this.isNearLive) {
               // ADJUSTED LIVE POSITION / REPOSITIONING
               // mysterious DASH offset when pausing live and postitioning replay stream
               if (this.isDash() && !this.isLiveSource) this.initialStreamPositionUnix -= 37000
               this.streamLiveDelay = liveOffset
               //this.initialStreamPositionUnix += liveOffset.valueOf()
               this.replayMargin = moment.duration(40, "seconds")
            }
            console.debug("SOURCE is %o", this._source)
            if (safariMainVersion() >= 15 /*&& this._source?.type === SourceTypeList.ChannelReplay*/) {
               //console.debug("openStreamAsync 15")
               // TVV-21: Set to 0 for marker D position
               //this.initialStreamPositionApi = -15//-10 //-30
               this.initialStreamPositionApi = 0//-10 //-30
            }
            //window.ql_streamPosUnix = this.initialStreamPositionUnix / 1000
         }

         //this._api?.ttml({ type: "application/ttml+xml", src: this._source.getStreamUrl })
			const streamUrl = this.getStreamUrl()
         console.debug("SET SRC", this.__path, streamUrl, this.isDash() ? "application/dash+xml" : "application/x-mpegURL")
         //this._api?.reset?.()

         // start keepAlive
         if (this._source.type !== SourceTypeList.AdPlaylist) yield this._source.playlist.startKeepAlive()

         yield this._api?.src({
            //src: "https://cph-p2p-msl.akamaized.net/hls/live/2000341/test/master.m3u8",
            //src: "https://dash.akamaized.net/dash264/TestCasesIOP33/adapatationSetSwitching/5/manifest.mpd",
            //src: "https://livesim.dashif.org/livesim2/testpic_2s/Manifest_thumbs.mpd",
            //src: "https://livesim.dashif.org/livesim2/testpic_6s/multiaudio.mpd",
            // subtitle sample
            //src: "https://dash.akamaized.net/akamai/test/caption_test/ElephantsDream/elephants_dream_480p_heaac5_1_https.mpd",
            src: streamUrl,
            // to provoke error
            //src: 'https://example.com/invalid',
            //src: "https://devstreaming-cdn.apple.com/videos/streaming/examples/img_bipbop_adv_example_fmp4/master.m3u8",
            //type: "application/x-mpegURL",
            type: this.isDash() ? "application/dash+xml" : "application/x-mpegURL",
				    //children: [],
            // MTVW-743: firstWebkitneedkeyTimeout is a critial value on Safari, reduced from 100 to 10 ms
            firstWebkitneedkeyTimeout: 10,
            // setting keySystems also in the source, avoids errors in vhs.waitForKeySessionCreation (Object.keys(sourceKeySystems, sourceKeySystems would be null )
            keySystems: this._configureDrm()
         })
			/*
			this._api?.removeClass('vjs-fluid')
			this._api?.removeClass('vjs-16-9')
			this._api?.removeClass('vjs-4-3')
			*/
			//this._api?.removeClass('vjs-fill')
         //this._api?.load()
         console.debug("options:", this._api?.options(), videojs.options)
         let qualityLevels = null
         try {
            qualityLevels = this._api?.qualityLevels()
            console.debug("qualityLevels", qualityLevels)
         }
         catch (e) {
            console.error("CAUGHT", e)
         }

         const self = this
         qualityLevels?.on('change', function () {
            console.debug('New quality level: ix %s, %o, all %o', qualityLevels.selectedIndex, qualityLevels[qualityLevels.selectedIndex], qualityLevels)
            // MTVW-774: test
            /*
            if (this._source?.type !== SourceTypeList.ChannelLive &&
               isAndroid() && (browserName().indexOf("chrome") >= 0 || browserName().indexOf("edge") >= 0)) {
               for (var i = 0; i < qualityLevels.length; i++) {
                  if (i !== qualityLevels.selectedIndex) {
                     console.debug("disable quality", i)
                     qualityLevels[i].enabled = false
                  }
               }
            }
            console.debug('quality levels: ix %s, %o, all %o', qualityLevels.selectedIndex, qualityLevels[qualityLevels.selectedIndex], qualityLevels)
            */
            // MTVW-794
            const bitrate = parseInt(getUrlQueryParam("bitrate"), 10)
            /*
            if (self.__path === "playerAds") {
               bitrate = 1357583
               console.debug("bitrate=", bitrate)
            }
            */
            console.debug("bitrates", self.__path, qualityLevels.levels_.map((e, i) => ((i === qualityLevels.selectedIndex ? "->" : "") + e.bitrate)).join(' '))
            if (bitrate) {
               // check if in the list
               const found = qualityLevels.levels_.filter(i => {
                  return i.bitrate === bitrate
               })
               console.debug("locking bitrate %s", bitrate, (found.length === 1) ? "FOUND" : "NOT FOUND")
               if (found.length === 1) {
                  for (var i = 0; i < qualityLevels.length; i++) {
                     if (qualityLevels[i].bitrate !== bitrate) qualityLevels[i].enabled = false
                  }
               }
            }
         })

         //if (this.isLiveSource === true) {
         //if (isIphone()) yield this.setPlay()
         //return
         //}
         //this._api?.load() // causes NotAllowedError

         // wait for videojs API
         this._startWatchdog()
         console.debug("wait for _waitPlayer", this.__path, this._waitPlayer)
         yield this._waitPlayer.waitAsync()
         console.debug("_waitPlayer SUCCEEDED", this.__path)

         if (this.isFairplay() || this.isWidevine()) {
            const self = this
            this.tech?.on('licenserequestattempted', function (event) {
               // Act on event
               console.debug("eme: licenserequestattempted", event)
            })
            this.tech?.on('keystatuschange', function (event) {
               // Event data:
               // keyId
               // status: usable, output-restricted, etc
               // target: the MediaKeySession object that caused this event
               console.debug("eme: keystatuschange", event)
            })
            this.tech?.on('keysessioncreated', function (event) {
               // note that there is no event data for keysessioncreated
               console.debug("eme: keysessioncreated", event)
            })
         }


         // reset again to avoid to load channel faster
         // VJS: this causes problems
         //this.setStop()
         // ICOMMENT this._waitStream.setReset()

         this.setIsLoading(true, "after _waitPlayer")
         appStats.player = this.version

         /*
         // wait for stream loading so we can manage player stream
         this._startWatchdog()
         yield this._waitStream.waitAsync()
         this._isStreamLoaded = true
         //this.isStreamLoaded = true
         this.setPlay("_waitStream canplay")
         //console.debug("after _waitStream", this.error)
         // At this point 'loadstart' has been fired
         console.debug("_waitStream SUCCEEDED", this.__path)
         // MTVW-512, MTVW-517: Playlist not found error handling, not necessary when using setReject(error) instead of setResolve()
         //if (this.error) throw this.error
         */

         //this._api?.pause()

         // wait for stream loading so we can manage player stream
         this._startWatchdog()
         console.debug("wait for _waitStream", this.__path)
         yield this._waitStream.waitAsync()
         // At this point 'loadstart' has been fired
         console.debug("_waitStream SUCCEEDED", this.__path)
         this._isStreamLoaded = true
         // MTVW-743: Some individual dash (widevine) replay streams were stuck after the eme requests (loadeddata was not triggered),
         // therefore we start playing here and pause the stream. Play decision is done later
         //if (this.__path !== "playerAds") {
         this.isStreamLoaded = true
         if (this.streamStartPosition) {
            console.debug("setting streamStartPosition=%s (%s)", this.streamStartPosition, moment(this.streamStartPosition).utc().format(TIME_FORMAT_BACKEND))
            this.setPositionTimeStamp(this.streamStartPosition)
         }
         yield this.setPlay("after loadstart")
         if (!this.isLiveSource && this.__path !== "playerAds"/*this.preloadReq*/) {
            console.debug("pausing api")
            // NOTE: pausing live stream on Safari sometimes causes problems
            this._api.pause()
            this.setIsPaused(true)
         }

         /*
         return
         yield this._waitPaused.waitAsync()
         */
         /*
         if (this.streamStartPosition) {
            console.debug("jump/seek to offset streamStartPosition=%s (%s)", this.streamStartPosition, moment(this.streamStartPosition).utc().format(TIME_FORMAT_BACKEND))
            this.setPositionTimeStamp(this.streamStartPosition)
         }
         */


         // MTVW-512, MTVW-517: Playlist not found error handling, not necessary when using setReject(error) instead of setResolve()
         //if (this.error) throw this.error

         console.debug("initialStreamPosition %s Api=%o Unix=%o (%o)", this.__path, this.initialStreamPositionApi, this.initialStreamPositionUnix, moment(this.initialStreamPositionUnix).utc().format(TIME_FORMAT_BACKEND), this._source.playlist)
         //console.debug("_apiGetPosition %s", this._apiGetPosition())
         if (this._source.playlist.seekable && this._source.playlist.eventStart > 0) {
            //console.debug("jump/seek to offset eventStart=%s (%s)", this._source.playlist.eventStart, moment(this._source.playlist.eventStart).utc().format(TIME_FORMAT_BACKEND))
            //console.debug("position 3j %o %o (%o)", this._apiGetPosition(), moment(this._brokenApiPlayTimeTs() || 0).format(TIME_FORMAT_BACKEND), moment().format(TIME_FORMAT_BACKEND))
            //ICOMMENT this._waitSeekCompleted.setReset()
            const seekRange = this._api?.buffered()
            console.debug("seekable", seekRange.start(0), seekRange.end(0))
            /* TODO VJS
            const seekRange = this._api?.seekable()
            console.debug("seekable range %o -> %o, %o", seekRange.start, seekRange.end, this._api?.seekable())
            */
            //this.setPosition(this._source.playlist.eventStart)
            //this.streamStartPosition = preload && startPosition ? startPosition : this._source.playlist.eventStart
            //this.isNearLive = moment().utc().valueOf() <= this._source.playlist.end
            console.debug("streamStartPosition %s, isNearLive %s playlist %o", moment(this.streamStartPosition).utc().format(TIME_FORMAT_BACKEND), this.isNearLive, this._source.playlist)

            // CHANGE FOR AUTOPLAY false
            if (!this.isNearLive) yield this._waitSeekCompleted.waitAsync()
            console.debug("_waitSeekCompleted SUCCEEDED: position after seek %o %o (%o)", this._apiGetPosition(), moment(this._brokenApiPlayTimeTs() || 0).format(TIME_FORMAT_BACKEND), moment().format(TIME_FORMAT_BACKEND))
            console.info("@@openStreamAsync() jump/seek completed")
         }
         //this.hidePosterAtSeekComplete = true
         console.debug("position now %s %o %o (%o)", this.__path, this._apiGetPosition(), moment(this._brokenApiPlayTimeTs() || 0).format(TIME_FORMAT_BACKEND), moment().format(TIME_FORMAT_BACKEND))

         // MTVW-92: Why was it reset?
         //this.setSelectSubtitleTrack(
         //this.isStreamLoaded = true

         console.debug("_isPausedUser %s %s %s preload %s", this.__path, this._isPausedUser, this.isPaused, preload)
         if (!this._isPausedUser) {
            console.debug("@@openStreamAsync() start playing", this.__path)
            // set play
            if (this.isLiveSource || this.__path === "playerAds" /*!preload*/) {
               if (this.apiGuard?.paused()) yield this.setPlay("!preload")
            }
            else {
               /*
               if (startPosition) {
                  console.debug("SET startPosition %s", startPosition)
                  this.setPositionTimeStamp(startPosition)
                  // TODO VJS: added, recheck
                  //yield this._waitSeekCompleted.waitAsync()
               }
               */
               // TODO VJS: added, recheck
               this._clearWatchdog()
               this.setPause()
               this.setIsPosterVisible(true, "!_isPausedUser")
               //this.setIsPausedUser(true)
               // MTVW-531, MTVW-529: Direct execution within player callback may cause impredicable behavior on Safari
               //setTimeout(() => {
               // GT12
               // MTVW-444
               if (this.__parent.playerFg) {
                  yield this.setPlay("playerBg")
               }
               console.debug("calling preloadEvent, isPaused", this.isPaused)
               if (!getUrlQueryParam("adUrl")) yield this._source?.preloadEvent?.(/*true*/)
               console.debug("setPlayResult", this.setPlayResult)
               //})
            }

            // workaround due to chrome and safari autoplay policy
            //if (this._api?.paused() && !preload && this.__path !== "playerAds") {
            // VJS Safari
            // MTVW-700: replace this._api.isPaused() with !this.setPlayResult
            if (!this.setPlayResult && !preload && this.__path !== "playerAds") {
               /*
               this.setMutedByBrowser(true)
               // MTV-1781: set also local mute state!
               this.setMuteState(true)
               this._api?.muted(true)
               this.setPlayResult = true
               console.debug("setPlay from this._api?.paused()")
               yield this.setPlay()
               */
               yield this.setPlay("autoplay muted")
            }
            // MTVW-700: Attempt for paused / muted issue on Safari
            /*
            else if (this.setPlayResult && this._api?.paused() && !preload && this.__path !== "playerAds") {
               setTimeout(() => {
                  this._api?.play()
               }, 100)
            }
            */
            else if (!preload) {
               setTimeout(() => {
                  if (this.setPlayResult && this.apiGuard?.paused() && this.__path !== "playerAds") {
                     this.apiGuard?.play()
                  }
               }, 200)
            }
         }
         //else this.setPlay()

         /*
         this._api?.play().catch((e) => {
            console.error("caught", e)
         })
         */
         // player.hls is deprecated. Use player.tech().vhs instead
         //console.debug("this %o, %o", this, this._api?.hls)

         //console.debug("position 5 %o %o (%o)", this._apiGetPosition(), moment(this._brokenApiPlayTimeTs() || 0).format(TIME_FORMAT_BACKEND), moment().format(TIME_FORMAT_BACKEND))

         // MTV-3040: Move to event VO_OSMP_CB_PLAY_STARTED
         // hide poster ready to go
         /*
         this.setIsPosterVisible(false)
         this.setIsLoading(false)
         */

         // changed, do it before loading the source 
         //if (this._source.type !== SourceTypeList.AdPlaylist) yield this._source.playlist.startKeepAlive()
         this.openStreamDone = true
         // MTVW-545: notify onboarding dialog
         window.postMessage({ request: 'openStreamDone' })
         if (this.error) {
            throw this.error
         }

         // ensure that poster is hidden
         //console.debug("setIsPosterVisible false setTimeout")
         if (!preload) this.setIsPosterVisible(false, "openStreamAsync timeout", false, 500)

         //videojs.options.html5.nativeTextTracks = true
         console.debug("@@openStreamAsync() <- this._source=%o", this._source)
         // Test purpose
         /*
         setTimeout(() => {
            this._source?.playCompleteEvent?.(this.positionTimeStamp, this.durationMetaData)
         }, 20000)
         */

      } catch (e) {
         console.error("@@openStreamAsync().catch(e=%o)<- this._source=%o", e, this._source, this.__path)
         console.debug("aborted= %s, idDomplayer %s", e === WAIT_ABORT, this.idDomPlayer)
         this._clearWatchdog()
         //this.setReject("IGNORE")    // would cause issues when switching live channels
         if (e?.indexOf?.(WAIT_ABORT) >= 0) this._playerWatchdog("waitAbort")
         // why is e sometimes null (with MTVW-682)?
         //if (e) throw e
      }
   })

   setReject(reason = WAIT_ABORT) {
      console.log("@")
      // passing the string below doesn't stop the player!
      // from the doc: "If with parameter, send stop request event to Verspective Analytics Export"
      //this._api?.stop("TERMINATED_BY_USER")
      // TODO VJS
      //this._api?.stop?.()
      this.apiGuard?.pause?.()
      this._waitPlayer.setReject(reason)
      this._waitStream.setReject(reason)
      this._waitSeekCompleted.setReject(reason)
      this.setIsLoading(false, "setReject")
   }

   _updateInternal(eventName = null) {
      // triggered for player event, attention throttled to 1/sec may not receive all event
      console.log("@(eventName=%o .isPlayerActive=%o)", eventName, this.isPlayerActive)
      if (!this.isPlayerActive) {
         return void 0
      }

      // console.debug("position : %o %o (%o)", this._apiGetPosition(), moment(this._brokenApiPlayTimeTs() || 0).format(TIME_FORMAT_BACKEND), moment().format(TIME_FORMAT_BACKEND))

      // MTVW-444: keep time display for TV player when playerBg is not visible
      //if (this.__parent.playerFg) return
      const timeStamp = rootStore.time.getTimeStamp()
      // MTVW-768: !this.isLoading, avoid garbage stamp
      // NOTE: that in very rare cases, this._apiGetPosition() still might return a unix time stamp instead
      // of a relative position in seconds
      if (!this.isLoading)
         this.currentStreamPositionApi = this._apiGetPosition()
      //console.debug("_updateInternal THIS %o", this._source)
      if (safariMainVersion() >= 15 && this._source?.type === SourceTypeList.ChannelReplay) {
         //console.debug("_updateInternal 15")
         //this.currentStreamPositionApi += 10
      }

      let offsetSeconds = 0
      if (this.isStreamLoaded) {
         if (this.isLiveSource) {
            if (!this._isPausedUser) {
               // ADJUSTED LIVE POSITION / REPOSITIONING
               //this.positionTimeStamp = this._brokenApiPlayTimeTs()
               offsetSeconds = this.currentStreamPositionApi - this.initialStreamPositionApi
               this.positionTimeStamp = this.initialStreamPositionUnix + (offsetSeconds * 1000)
               this.streamCurrentDelay = Math.floor((timeStamp - this.positionTimeStamp) / 1000)
            }
         } else {
            //console.debug("currentStreamPositionApi %s, initialStreamPositionApi %s, initialStreamPositionUnix %s", this.currentStreamPositionApi, this.initialStreamPositionApi, this.initialStreamPositionUnix)
            offsetSeconds = this.currentStreamPositionApi - this.initialStreamPositionApi
            //console.debug("offsetSeconds %s", offsetSeconds)
            //console.debug("initialStreamPositionUnix %s, offsetSeconds %s", this.initialStreamPositionUnix, offsetSeconds)
            this.positionTimeStamp = this.initialStreamPositionUnix + (offsetSeconds * 1000)
            this.streamCurrentDelay = 0
         }
         //console.debug("pts %s, offS %s", this.positionTimeStamp, offsetSeconds)
         //console.debug("positionTimeStamp60s %s, %s", this.positionTimeStamp60s, Math_floor(this.positionTimeStamp, 60000))
         this.positionTimeStamp60s = Math_floor(this.positionTimeStamp, 60000)
      }

      // growing stream === Live stream or RollingBuffer with end in the future
      this.isStreamGrowing = this._apiIsLive()

      // GT12
      //if (this.isAdSource === true) console.debug("stamp %s", this.positionTimeStamp, moment(this.positionTimeStamp).utc().format(TIME_FORMAT_BACKEND))
      // MTVW-531, MTVW-529: Direct execution within player callback may cause impredicable behavior on Safari
      setTimeout(() => {
         //console.debug("positionTimeStamp", this.__path, this.isLoading, this.positionTimeStamp, offsetSeconds, this.currentStreamPositionApi, this.initialStreamPositionApi)
         // MTVW-768: !this.isLoading, avoid garbage stamp
         if (!getUrlQueryParam("adUrl") && !this.isLoading)
            this._source?.timeStampEvent?.(this.positionTimeStamp, offsetSeconds, this.playCompleted)
      })

      // TODO VJS
      if (eventName !== "interval_check") {
         this.rangeStartStruct.tick(timeStamp)
         this.rangeEndStruct.tick(timeStamp)
         //this.liveDelayStruct.tick(timeStamp)
      }
   }
   // getters
   get isStreamSequential() {
      // TODO VJS: check
      if (!this._isStreamLoaded) {
         return null
      }
      if (!this.isStreamGrowing) {
         return true
      }
      //@todo
      if (this._source.type === SourceTypeList.ChannelReplay) {
         return true
      }
      // TODO VJS:
      //return (this._apiGetBufferDuration() < 20) || (this._apiGetBufferDuration() > this.liveDelayStruct.value + 20)
      console.debug("isStreamSequential?", true)
      return true
   }

   isJumpFwdPossibleSec(sec) {
      return true
      /*
      if (!this._isStreamLoaded) {
         return false
      }
      if (this.isLiveSource) {
         return false
      }
      const secondsToEnd = moment(this.maximumPositionTimestamp).diff(moment(this.positionTimeStamp), "seconds")
      //console.debug("remaining Fwd", secondsToEnd)
      return secondsToEnd > sec
      */
   }

   isJumpRwdPossibleSec(sec) {
      return true
      /*
      if (!this._isStreamLoaded) {
         return false
      }
      if (this.isLiveSource) {
         return false
      }
      const secondsToStart = moment(this.positionTimeStamp).diff(moment(this.minimalPositionTimestamp), "seconds")
      //console.debug("remaining Rwd", secondsToStart)
      return secondsToStart > sec
      */
   }

   get rangeTimeStampStart() {
      // MTVW-543: fix rangeTimeStampStart
      //return this.positionTimeStamp - this.currentStreamPositionApi * 1000
      return this.initialStreamPositionUnix - this.initialStreamPositionApi * 1000
   }

   get rangeTimeStampEnd() {
      //if (this.isLive) {

      //  MTVW-543: fix rangeTimeStampEnd
      //return this.positionTimeStamp + this.rangeEndStruct.value * 1000
      return this.initialStreamPositionUnix + (this.rangeEndStruct.value - this.initialStreamPositionApi) * 1000
      //}
      //return this.position + this.streamCurrentDelay * 1000
      //return this.rangeEndStruct.calculated
   }
   // actions
   setVolume(volume, isInit = null) {
      if (this._source?.isOnDiscover) this.apiGuard?.muted(true)
      else if (volume >= 0 && volume <= 100) {
         this.apiGuard?.volume?.(volume / 100)
         this.settings.volume = volume
         if (isInit !== true && volume > 0 && this.isMuted) {
            this.setMuteState(false)
         }
      }
   }

   setMuteState(bState = null) {
      //console.debug("setMuteState", this.__path, this._api, this)
      // MTVW-682: try/catch and error message (shouldn't be necessary anymore with the check in _checkForExistingManifest)
      try {
         if (!this.apiGuard) return
         if (this._source?.isOnDiscover) {
            this._api.muted(true)
            return
         }
         if (bState === null) {
            //console.debug("setMuteState 1", bState)
            bState = !this.isMuted
         }
         if (bState) {
            //console.debug("setMuteState 2", bState)
            this._api.muted(true)
         } else {
            // can generate: Unhandled Rejection (TypeError): Cannot read properties of null (reading 'media')
            // it happens if there is a 404 after the start add (manifest not found)
            //console.debug("setMuteState 3", bState)
            this._api.muted(false)
         }
         this.settings.isMuted = bState
         //console.debug("setMuteState 4", this.settings.isMuted)
      }
      catch (e) {
         console.error("setMuteState", e)
         const error = ErrorBase.CreateError(["Player", "Media", "Kommunikationsfehler"], null, { code: "404?", type: "error.type" })
         rootStore.page.Player.error = error
      }
      return this
   }

   getPlaylistStart() {
      // Safari does not support MSE (Media Source Extension)
      console.debug("currentTime %s, duration %s", this.tech?.vhs?.stats?.currentTime, this.tech?.vhs?.stats?.duration, this.tech)
      const playlistStart = this.tech?.vhs?.playlists?.media?.()?.segments[0]?.dateTimeString
      console.debug("playlist dateTime %s, media %o", playlistStart, this.tech?.vhs?.playlists?.media?.() /*, this.tech?.vhs.playlists?.media?.().segments[0]?.dateTimeObject*/)
      return playlistStart ? playlistStart : null
   }

   // VJS Safari: flow function
   setPlay = flow(function* (info = "none") {
      //console.debug("setPlay source %o, isStreamLoaded %s", this._source, this.isStreamLoaded)
      this.setPlayResult = false
      this.playCompleted = false
      console.debug("setPlay %s, %s, %s", info, this.__path, this.isStreamLoaded /*, new Error().stack*/)
      if (this.isStreamLoaded) {
         //console.debug("calling _api.play")
         // MTV-2134 / MTV-2967: Deal with unhandled rejection in browser's autoplay policy
         // MTVW-531: Safari workaround. this._api?.play() does not always continue at the paused position
         //console.debug("path", this.__path)
         /**/ // TODO VJS: recheck
         /*
         if (isSafari() && this.positionTimeStamp && this.isPausedUser) {
            if (this.__path === "playerAds") {
               this._updateInternal()
               //console.debug("Safari this._api?.setPosition %s, src %o, this %o", (this.positionTimeStamp - this.initialStreamPositionUnix) / 1000 + this.initialStreamPositionApi, this._source, this)
               this._api?.currentTime((this.positionTimeStamp - this.initialStreamPositionUnix) / 1000 + this.initialStreamPositionApi)
            }
            else {
               //console.debug("Safari positionTimeStamp %s, src %o, this %o", this.positionTimeStamp, this._source, this)
               this.setPositionTimeStamp(this.positionTimeStamp)
            }
         }
         */
         try {
            this.setIsPausedUser(false)
            yield this._api?.play()
            // autoplay doesn't return a promise! iOS (live) stream will be paused when using autoplay
            //yield this._api?.autoplay("muted")
            this.setPlayResult = true
            console.debug("setPlay started successfully %s, %o, player %s", info, this.positionTimeStamp, moment(this.positionTimeStamp).utc().format(TIME_FORMAT_BACKEND), moment().utc().format(TIME_FORMAT_BACKEND), this.__path)
            //this.setMuteState(this.isMuted)
            //this.getPlaylistStart()
            //console.debug("dimensions=", this, this._api?.currentDimensions(), this._api?.aspectRatio(), this._api?.videoHeight(), this.videoNode?.videoHeight, this.videoNode?.videoWidth)
         }
         catch (error) {
            console.error("setPlay failed with %s, %s, %s", error.name, error.message, this.__path)
            //MTVW-217 / MTVW-127 / MTVW-72
            //console.debug("setIsPosterVisible false catch")
            this.setIsPosterVisible(false, "catch error")
            if (error.name === "NotAllowedError") {
               if (!this._source?.isOnDiscover) this.setMutedByBrowser(true)
               // MTV-1781: set also local mute state!
               this.setMuteState(true)
               this._api?.muted(true)
               this.setPlayResult = false
            }
            else {
               this.setPlayResult = false
               console.error("setPlay error", info, error)
               ///throw error
            }
            //else throw error
            //this.setPlayResult = true
            /*
            console.error("setPlay NotAllowedError, this._api?.paused()")
            try {
               yield this._api?.play()
            }
            catch (err) {
               console.error("CAUGHT in setPlay", err)
            }
            */

            /*
            this.setPlayResult = false
            // MTV-1781: set also local mute state!
            this.setMuteState(true)
            this._api?.muted(true)
            // MTVW-589: setForcedPause added and commented
            //monitorState.setForcedPause(false)
            //this.setIsPausedUser(true)
            */
            ////}
         }
         finally {
            try {
               // apply initial volume settings
               console.debug("setting volume")
               // TODO: volume?
               this.setVolume(this.settings.volume, true)
               this.setMuteState(this.isMuted)
               //console.debug("audioTracks=", this._api?.audioTracks())
               this.audioTracksContainer.setTracksFromStream()
               // MTVW-731: subtitles are not working with Dash/Widevine (AppError generated in player)
               // DIV-40157
               if (!this._source?.isOnDiscover) {
               /*if (!this.isDash())*/ this.subtitleTracksContainer.setTracksFromStream()
               }
            }
            catch (e) {
               onsole.error("setPlay finally failed", e, this.__path)
            }
         }
         this.setIsPausedUser(false)
         this.setIsPaused(false)
      }
      //this.setIsPosterVisible(false)
      return this
   })

   //setStop() {
   setStop = flow(function* () {
      console.debug("setStop")
      try {
         if (this._waitPlayer.isDone === true) {
            ////if (this._api) this._api?.stop()
            // TODO VJS: recheck
            // VJS reset sets volume to 100
            const volume = this.settings.volume
            //this._api?.reset?.()
            // TODO: volume?
            //this.setVolume(volume)
            //this._api?.pause?.()
            this._clearWatchdog()
         }
      }
      catch (err) {
         console.error("CAUGHT in setStop", err)
      }
      return this
   })

   // MTVW-764
   clearSubtitles() {
      try {
         if (this.isDash()) {
            const renderDiv = document.getElementById("imscContainer")
            const subtitleActive = renderDiv?.getElementsByTagName("div")[0]
            if (subtitleActive) {
               renderDiv.removeChild(subtitleActive)
            }
         }
      }
      catch (e) {
         console.error("clearSubtitles", e)
      }
   }

   // reset player, will invoke stopKeepAlive
   stopInstance = flow(function* () {
      // MTVW-764
      this.clearSubtitles()
      // MTVW-788, MTVW-787: kill player
      const cRefElem = this._refElemContainer
      //this._api.reset()
      if (cRefElem) {
			//yield this.setRefElemContainer(null, (this.__path === "playerAds") ? true : !switchToLive)
			yield this.setRefElemContainer(null, true)
      	yield this.setRefElemContainer(cRefElem, true)
      }
		else console.warn("stopInstance no refElem")
      this._clearWatchdog()
   })

   setClose = flow(function* () {
      console.debug("setClose")
      if (this._source?.playlist && this._source.type !== SourceTypeList.AdPlaylist) {
         yield this._source.playlist.stopKeepAlive()
      }
      this._clearWatchdog()
      // TODO VJS: recheck
      //this._api?.close()
      //this._api?.reset()
      return this
   })

   // MTVW-588: stop invoking updateCheck
   setPlayCompleted() {
      console.debug("setPlayCompleted", this.__path)
      /* disposing does not work
      if (!this._api?.isDisposed()) {
         this.setRefElemContainer(null, false)
         this.playCompleted = true
      }
      return
      */
      //this._api?.reset() // does not work
      //this._api?.pause() // can cause errors with ads
      //this._refElemContainer = null  // setting to null does not work for the 2nd ffAd
      this.playCompleted = true
      clearInterval(this._intervalId)
      this._intervalId = null
   }

   setPause() {
      console.log("@")
      if (this.isStreamLoaded) {
         this._api?.pause?.()
         this.setIsPausedUser(true)
         // comment ?
         this.activePosition = this.positionTimeStamp
         // MTVW-816
         if (this.openStreamDone) rootStore.page.CoCoBookmarks.notifyPaused(this, this.positionTimeStamp)
         console.debug("setPause", this.positionTimeStamp, this.__path)
      }
      this.setIsPosterVisible(false, "setPause")
      return this
   }

   setPosition(sec) {
      this._setPositionInternal(sec)
      return this
   }

   setPositionRelatively(sec) {
      console.debug("@(rel sec=%o)", sec)
      this._setPositionInternal((this.positionTimeStamp) + sec * 1000)
      return this
   }

   setJumpLive() {
      console.debug("@(live)")
      // MTVW-543
      this._setPositionInternal(moment().utc().valueOf())
      return this
   }

   setPositionTimeStamp(timeStamp) {
      console.debug("@(timeStamp=%o %o, %s)", timeStamp, moment(timeStamp).utc().dump(), moment(timeStamp).utc().valueOf())
      //this.hidePosterAtPlayStarted = false
      // MTVW-543
      this._setPositionInternal(moment(timeStamp).utc().valueOf())
      return this
   }

   setPositionTimeStampAsync = flow(function* (timeStamp) {
      console.debug("@(timeStampAsync=%o %o, %s)", timeStamp, moment(timeStamp).utc().dump(), moment(timeStamp).utc().valueOf(), this.positionTimeStamp - moment(timeStamp).utc().valueOf())
      // MTVW-1062: check applied in actionAfterFastForwardAd
      //if (Math.abs(moment(timeStamp).utc().valueOf() - this.positionTimeStamp) <= 500) return this
      this._waitSeekCompleted.setReset()
      this._clearWatchdog()
      this._setPositionInternal(moment(timeStamp).utc().valueOf())
      try {
         yield this._waitSeekCompleted.waitAsync()
      }
      catch (e) {
         console.error("setPositionTimeStampAsync", e)
         return Promise.reject(e)
      }
      //console.debug("after setPositionTimeStampAsync")
      return this
   })

   setJumpFwd(sec = null) {
      if (sec === null) {
         sec = this.settings.jumpFwdSec
      }
      console.debug("(sec=%o)", sec)
      this.clearSubtitles()
      if (this.isJumpFwdPossibleSec(sec) && this.isStreamSequential) {
         this.setPositionRelatively(sec)
      }
      return this
   }

   setJumpRwd(sec = null) {
      if (sec === null) {
         sec = this.settings.jumpRwdSec
      }
      console.info("@(sec=%o) -> start", sec)
      console.debug("setJumpRwd isStreamSequential %s", this.isStreamSequential)
      this.clearSubtitles()
      if (this.isStreamSequential) {
         this.setPositionRelatively(-sec)
      }
      return this
   }

   // MTVW-727
   isPipMode() {
      //console.debug("calling isInPipMode", this._api?.isInPictureInPicture())
      //return this.isInPip
      return this._api?.isInPictureInPicture()
   }

   showPiP = flow(function* (enable) {
      // MTVW-727b
      if (this.__path === "playerAds") return
      try {
         if (enable) {
            const safariMac = !isIpad() && browserOs().toLowerCase().indexOf("mac os") >= 0
               && browserName().toLowerCase().indexOf("safari") >= 0
            // leaving fOullscreen on Safari Mac will not open PiP afterwards
            if (this.isOnFullScreen() && !safariMac /*&& isAndroid()*/) {
               console.debug("showPip exitFullScreen")
               try {
                  yield this.setFullScreen(false)
               }
               catch (err) {
                  console.error("showPip exitFullScreen", err)
               }
               //yield sleep(100)
               console.debug("showPip exitFullScreen done")
            }
            yield this._api?.requestPictureInPicture()
            // MTVW-727b
            this.__parent.setPiPPlayer(this)
         }
         else {
            // avoid excecption, check for PiP
            if (this.isInPip) yield this._api?.exitPictureInPicture()
            // MTVW-727b
            this.__parent.setPiPPlayer(null)
         }
      }
      catch (err) {
         console.debug("showPiP enable", enable, err)
         if (this.isInPip) {
            try {
               yield this._api?.exitPictureInPicture()
            }
            catch (e) {
               this.__parent.setPiPPlayer(null)
               this.isInPip = false
            }
         }
      }
   })

   _startWatchdog() {
      this._clearWatchdog()
      this.watchdogTimerId = setTimeout(() => {
         console.debug("watchdog elapsed visibility %s, %s, positionTimeStamp %s", document.visibilityState, this.__path, this.positionTimeStamp, moment(this.positionTimeStamp).utc().format(TIME_FORMAT_BACKEND))
         /// MTVW-493: timeout happened sometimes when replay ad was immediately paused for longer time
         if (this.__path === "playerAds") return
         runInAction(() => {
            // MTVW-829: The player or possibly the browser behaves differently in Live Tv in background muted state.
            // The VJS 'waiting' event may timeout and causes issues. Disable watchdog trigger in background.
            // A spinner might be shown when switching to foreground but should disappear after a few seconds.
            if (document.visibilityState === "hidden") {
               this._clearWatchdog()
               return
            }
            // TODO: should not be necessary
            this.setIsPosterVisible(false, "_startWatchdog")
            this.watchdogTimerId = null
            this.setReject()
            this.positionTimeStamp = null
            this.positionTimeStamp60s = null
            // generate restart event
            this._playerWatchdog("wdElapsed")
         })
      }, 20000)
   }

   _clearWatchdog() {
      if (this.watchdogTimerId) clearTimeout(this.watchdogTimerId)
      this.watchdogTimerId = null
   }
}
